import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { ChromePicker, ColorResult } from 'react-color';
import { prepareFileForSubmission } from '../utils/fileHandler';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  List,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Checkbox,
  ButtonBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton,
  Drawer,
  alpha,
  Slider,
  Link,
  Paper,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Palette as PaletteIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ViewModule,
  ViewModule as ModernIcon,
  ViewStream as MinimalIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Clear as ClearIcon,
  Menu as MenuIcon,
  ChatBubbleOutline as ChatIcon,
  ChevronLeft as ChevronLeftIcon,
  DragHandle as DragHandleIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  PictureAsPdf as PdfIcon,
  Language as WebsiteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SubscriptionGate } from '../components/SubscriptionGate';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

interface ChatbotConfig {
  name: string;
  description: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  layoutStyle: 'modern' | 'minimal';
  logoUrl?: string;
  bannerUrl?: string;
  logoMaterialId?: string;
  bannerMaterialId?: string;
}

interface FileAttachment {
  name: string;
  size: number;
  type: string;
  dataUrl: string;
  inputType: string; // 'image', 'video', 'audio', 'pdf', etc.
}

interface Message {
  text: string;
  isUser: boolean;
  timestamp: string;
  credits?: number;
  attachments?: FileAttachment[];
}

interface ChatThread {
  id: string;
  title: string;
  lastMessage: string;
  timestamp: string;
  messages: Message[];
}

const LAYOUT_IMAGE_SPECS = {
  modern: {
    logo: { recommended: '220x220px', shape: 'Square (1:1)', description: 'Appears as a circular avatar next to the title' },
    banner: { recommended: '1200x260px', shape: 'Wide (roughly 5:1)', description: 'Large hero background behind the header content' }
  },
  minimal: {
    logo: { recommended: '120x120px', shape: 'Square (1:1)', description: 'Small rounded square icon next to the title' },
    banner: { recommended: '1200x200px', shape: 'Wide (roughly 6:1)', description: 'Subtle header backdrop with extra breathing room' }
  }
} as const;

const ensureLayoutStyle = (style?: string | null): 'modern' | 'minimal' =>
  style === 'minimal' ? 'minimal' : 'modern';

const normalizeHexColor = (color?: string | null): string => {
  if (!color) return '';
  const trimmed = color.trim();
  if (!trimmed) return '';
  const withoutHash = trimmed.replace(/^#*/, '');
  if (!withoutHash) return '';
  const normalized = withoutHash.length === 3
    ? withoutHash.split('').map(char => char + char).join('').slice(0, 6)
    : withoutHash.slice(0, 6);
  return `#${normalized.toLowerCase()}`;
};

const DEFAULT_BRANDING_COLORS: Record<'primaryColor' | 'secondaryColor' | 'accentColor', string> = {
  primaryColor: '#9c27b0',
  secondaryColor: '#7b1fa2',
  accentColor: '#e91e63'
};

const DEFAULT_DESCRIPTION = 'Design and deploy your custom chatbot interface';

const getDescriptionSnippet = (text?: string, maxLength: number = 160) => {
  if (!text) return DEFAULT_DESCRIPTION;
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  const slice = normalized.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(' ');
  const safeSlice = lastSpace > 60 ? slice.slice(0, lastSpace) : slice;
  return `${safeSlice}…`;
};

export const ChatbotStudio: React.FC = () => {
  const { functionId } = useParams<{ functionId: string }>();
  const navigate = useNavigate();
  const { user, getEffectiveCustomerTag } = useAuth();
  
  const [chatbot, setChatbot] = useState<any>(null);
  const [branding, setBranding] = useState<ChatbotConfig>({
    name: '',
    description: '',
    primaryColor: DEFAULT_BRANDING_COLORS.primaryColor,
    secondaryColor: DEFAULT_BRANDING_COLORS.secondaryColor,
    accentColor: DEFAULT_BRANDING_COLORS.accentColor,
    layoutStyle: 'modern',
    logoMaterialId: undefined,
    bannerMaterialId: undefined,
  });
  const [apiKeys, setApiKeys] = useState<Array<{id: string; key: string; name: string}>>([]);
  const [selectedApiKey, setSelectedApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [removingAsset, setRemovingAsset] = useState<'logo' | 'banner' | null>(null);
  
  // Live chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<FileAttachment[]>([]);
  const [threadId, setThreadId] = useState<string>('');
  const [maxThreadLength, setMaxThreadLength] = useState(25);
  const [knowledgeCategories, setKnowledgeCategories] = useState<string[]>([]);
  const [categories, setCategories] = useState<Array<{id: string; name: string}>>([]);
  const [totalCreditsUsed, setTotalCreditsUsed] = useState(0);
  const [knowledgeChunks, setKnowledgeChunks] = useState(2);
  const [showCreditCounter, setShowCreditCounter] = useState(true);
  const [introMessage, setIntroMessage] = useState('');
  const [overrideTitle, setOverrideTitle] = useState('');
  const [originalInstructions, setOriginalInstructions] = useState('');
  const [editedInstructions, setEditedInstructions] = useState('');
  
  // Chat history state
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<string>('');
  const [showThreadDrawer, setShowThreadDrawer] = useState(false);
  
  const BEHAVIOR_TAB = 0;
  const LOOK_TAB = 1;
  const WEBSITE_TAB = 2;
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => (searchParams.get('tab') === 'website' ? WEBSITE_TAB : BEHAVIOR_TAB));
  const [justCreated, setJustCreated] = useState(() => searchParams.get('new') === '1');

  useEffect(() => {
    if (searchParams.get('new') === null && searchParams.get('tab') === null) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('new');
      next.delete('tab');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);
  
  // Iframe dialog state
  const [showIframeDialog, setShowIframeDialog] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const PREVIEW_MIN_HEIGHT = 420;
  const PREVIEW_MAX_HEIGHT = 1100;
  const [previewHeight, setPreviewHeight] = useState(720);
  const EXPORT_WARNING_STORAGE_KEY = 'chatbot_studio_export_warning_dismissed';
  type ExportAction = 'download' | 'iframe';
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [allowAttachments, setAllowAttachments] = useState(true);
  const [selectedModel, setSelectedModel] = useState('');
  const [exportConfirm, setExportConfirm] = useState<{
    open: boolean;
    action: ExportAction | null;
    remember: boolean;
    loading: boolean;
  }>({
    open: false,
    action: null,
    remember: false,
    loading: false,
  });
  
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (functionId && user) {
    loadChatbot();
    loadApiKeys();
    loadCategories();
      loadThreadsFromStorage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionId, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Calculate total credits when messages change
    const total = messages.reduce((sum, msg) => sum + (msg.credits || 0), 0);
    setTotalCreditsUsed(total);
  }, [messages]);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const generateUUID = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };

  const loadThreadsFromStorage = () => {
    const storageKey = `chatbot_threads_${functionId}`;
    const savedThreads = localStorage.getItem(storageKey);
    if (savedThreads) {
      const threads = JSON.parse(savedThreads);
      // Normalize thread titles - if they contain "System Role" or look like instructions, use chatbot name
      const chatbotName = overrideTitle || branding.name || chatbot?.display_name || chatbot?.name || 'Conversation';
      const normalizedThreads = threads.map((t: ChatThread) => {
        if (!t.title || t.title.includes('System Role') || t.title.includes('Objective:') || t.title.length > 100) {
          return { ...t, id: t.id || generateUUID(), title: chatbotName };
        }
        return { ...t, id: t.id || generateUUID() };
      });
      setThreads(normalizedThreads);
      if (normalizedThreads.length > 0 && !selectedThread) {
        setSelectedThread(normalizedThreads[0].id);
        setThreadId(normalizedThreads[0].id);
        setMessages(normalizedThreads[0].messages);
      }
    } else {
      // Create initial thread
      const newThreadId = generateUUID();
      const chatbotName = overrideTitle || branding.name || chatbot?.display_name || chatbot?.name || 'New conversation';
      const newThread: ChatThread = {
        id: newThreadId,
        title: chatbotName,
        lastMessage: '',
        timestamp: new Date().toISOString(),
        messages: introMessage ? [{
          text: introMessage,
          isUser: false,
          timestamp: new Date().toISOString()
        }] : []
      };
      setThreads([newThread]);
      setSelectedThread(newThreadId);
      setThreadId(newThreadId);
      setMessages(newThread.messages);
      saveThreadsToStorage([newThread]);
    }
  };

  const saveThreadsToStorage = (threadsToSave: ChatThread[]) => {
    const storageKey = `chatbot_threads_${functionId}`;
    localStorage.setItem(storageKey, JSON.stringify(threadsToSave));
  };

  const startNewConversation = () => {
    const newThreadId = generateUUID();
    const chatbotName = overrideTitle || branding.name || chatbot?.display_name || chatbot?.name || 'New conversation';
    const newThread: ChatThread = {
      id: newThreadId,
      title: chatbotName,
      lastMessage: '',
      timestamp: new Date().toISOString(),
      messages: introMessage ? [{
        text: introMessage,
      isUser: false,
      timestamp: new Date().toISOString()
      }] : []
  };

    setThreads(prev => {
      const updatedThreads = [newThread, ...prev];
      saveThreadsToStorage(updatedThreads);
      return updatedThreads;
    });
    setSelectedThread(newThreadId);
    setThreadId(newThreadId);
    setMessages(newThread.messages);
    setTotalCreditsUsed(0);
  };

  const selectThread = (thread: ChatThread) => {
    setSelectedThread(thread.id);
    setThreadId(thread.id);
    setMessages(thread.messages);
    // Recalculate credits for this thread
    const threadCredits = thread.messages.reduce((sum, msg) => sum + (msg.credits || 0), 0);
    setTotalCreditsUsed(threadCredits);
  };

  const deleteThread = (threadId: string) => {
    const updatedThreads = threads.filter(t => t.id !== threadId);
    setThreads(updatedThreads);
    saveThreadsToStorage(updatedThreads);
    
    if (selectedThread === threadId && updatedThreads.length > 0) {
      selectThread(updatedThreads[0]);
    }
  };


  const loadChatbot = async () => {
    try {
      const idToken = await user?.getIdToken();
      const response = await fetch(`/api/v2/ai-functions/${functionId}`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const func = data.data || data;
        setChatbot(func);
        const instructionsValue = func.instructions || '';
        setOriginalInstructions(instructionsValue);
        setEditedInstructions(instructionsValue);
        
        // Load existing branding from metadata
        console.log('Loading chatbot metadata:', func.metadata);
        if (func.metadata?.branding) {
          console.log('Loaded branding:', func.metadata.branding);
          // Set the complete branding object from metadata
          setBranding({
            name: func.metadata.branding.name || func.display_name || func.name,
            description: func.metadata.branding.description || func.description || 'AI Assistant',
            primaryColor: normalizeHexColor(func.metadata.branding.primaryColor) || DEFAULT_BRANDING_COLORS.primaryColor,
            secondaryColor: normalizeHexColor(func.metadata.branding.secondaryColor) || DEFAULT_BRANDING_COLORS.secondaryColor,
            accentColor: normalizeHexColor(func.metadata.branding.accentColor) || DEFAULT_BRANDING_COLORS.accentColor,
            layoutStyle: ensureLayoutStyle(func.metadata.branding.layoutStyle),
            logoUrl: func.metadata.branding.logoUrl || '',
            bannerUrl: func.metadata.branding.bannerUrl || '',
            logoMaterialId: func.metadata.branding.logoMaterialId,
            bannerMaterialId: func.metadata.branding.bannerMaterialId,
          });
        } else {
          setBranding({
            name: func.metadata?.chatbot_name || func.display_name || func.name,
            description: func.description || 'AI Assistant',
            primaryColor: DEFAULT_BRANDING_COLORS.primaryColor,
            secondaryColor: DEFAULT_BRANDING_COLORS.secondaryColor,
            accentColor: DEFAULT_BRANDING_COLORS.accentColor,
            layoutStyle: ensureLayoutStyle('modern'),
            logoMaterialId: undefined,
            bannerMaterialId: undefined,
          });
        }
        
        // Load settings from metadata
        if (func.metadata?.chat_history_length) {
          setMaxThreadLength(func.metadata.chat_history_length);
        } else {
          setMaxThreadLength(25); // Default to 25
        }
        if (func.metadata?.knowledge_chunks) {
          // Cap at 10 to match UI maximum
          setKnowledgeChunks(Math.min(func.metadata.knowledge_chunks, 10));
        } else {
          // Default to 2 if not set
          setKnowledgeChunks(2);
        }
        if (func.metadata?.intro_message) {
          setIntroMessage(func.metadata.intro_message);
        }
        if (func.metadata?.override_title) {
          setOverrideTitle(func.metadata.override_title);
        }
        if (func.metadata?.knowledge_categories) {
          setKnowledgeCategories(func.metadata.knowledge_categories);
        }
        if (func.metadata?.show_credit_counter !== undefined) {
          setShowCreditCounter(func.metadata.show_credit_counter);
        } else {
          setShowCreditCounter(true);
        }
        if (func.metadata?.allow_attachments !== undefined) {
          setAllowAttachments(func.metadata.allow_attachments);
        } else {
          setAllowAttachments(true); // Default to true
        }
        
        // Load selected model from metadata, or default to first available model
        if (func.metadata?.selected_model) {
          setSelectedModel(func.metadata.selected_model);
        } else if (func.allowed_models && func.allowed_models.length > 0) {
          setSelectedModel(func.allowed_models[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load chatbot:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApiKeys = async () => {
    try {
      if (!user) return;
      const idToken = await user.getIdToken();
      const res = await fetch('/api/v2/api-keys/', { 
        headers: { Authorization: `Bearer ${idToken}` } 
      });
      if (!res.ok) {
        console.error('API keys request failed:', res.status);
        return;
      }
      const data = await res.json();
      
      // Format keys properly - check the response structure
      let formattedKeys = [];
      if (data.data && Array.isArray(data.data)) {
        // Response has data property with array
        formattedKeys = data.data.map((k: any) => ({
        id: k.id,
        key: k.api_key || k.key,
        name: k.name || `Key ${k.id}`
      }));
      } else if (Array.isArray(data)) {
        // Response is directly an array
        formattedKeys = data.map((k: any) => ({
          id: k.id,
          key: k.api_key || k.key,
          name: k.name || `Key ${k.id}`
        }));
      }
      
      setApiKeys(formattedKeys);
      if (formattedKeys.length > 0) {
        setSelectedApiKey(formattedKeys[0].key);
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const idToken = await user?.getIdToken();
      const currentResp = await fetch('/api/v2/customers/current', {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (!currentResp.ok) return;
      const current = await currentResp.json();
      const customerId = current?.data?.customer_id;
      if (!customerId) return;
      
      // Load all categories with high limit
      const catsResp = await fetch(`/api/v2/customers/${customerId}/categories?limit=1000`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (catsResp.ok) {
        const cats = await catsResp.json();
        setCategories(cats.data?.items || []);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleImageUpload = async (type: 'logoUrl' | 'bannerUrl', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (limit to 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setSnackbar({ 
        open: true, 
        message: `File too large. Please select an image under 5MB.`, 
        severity: 'error' 
      });
      event.target.value = ''; // Reset the input
      return;
    }

    const isLogo = type === 'logoUrl';
    if (isLogo) {
      setUploadingLogo(true);
    } else {
      setUploadingBanner(true);
    }

    try {
      const idToken = await user?.getIdToken();
      const formData = new FormData();
      const assetName = `${chatbot.name}_${type === 'logoUrl' ? 'logo' : 'banner'}_${Date.now()}`;
      formData.append('name', assetName);
      formData.append('description', '');
      formData.append('file', file);
      
      // Determine category for upload
      const metadataCategories = Array.isArray(chatbot?.metadata?.knowledge_categories)
        ? chatbot.metadata.knowledge_categories
        : [];
      const possibleCategoryIds: string[] = [
        ...knowledgeCategories,
        ...metadataCategories,
      ];
      if (chatbot?.metadata?.primary_category_id) {
        possibleCategoryIds.push(chatbot.metadata.primary_category_id);
      }
      if (chatbot?.metadata?.category_id) {
        possibleCategoryIds.push(chatbot.metadata.category_id);
      }

      let categoryId: string | undefined;
      if (possibleCategoryIds.length > 0) {
        categoryId = possibleCategoryIds.find(catId => catId) || undefined;
      }
      if (!categoryId && categories.length > 0) {
        categoryId = categories[0].id;
      }

      if (categoryId) {
        formData.append('category_id', categoryId);
      }
      
      // Upload to materials endpoint
      const response = await fetch('/api/v2/materials/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const material = data.data || data;
        
      // Construct the proper URL for the uploaded material
      const bucketUrl = `https://storage.googleapis.com/${material.gcs_bucket}/${material.gcs_path}`;
      console.log('[ChatbotStudio] Setting image URL:', { type, url: bucketUrl });
      
      const materialIdKey = type === 'logoUrl' ? 'logoMaterialId' : 'bannerMaterialId';
      const updatedBranding: ChatbotConfig = {
        ...branding,
        [type]: bucketUrl,
        [materialIdKey]: material.id
      };
      setBranding(updatedBranding);
        
        setSnackbar({ open: true, message: `${isLogo ? 'Logo' : 'Banner'} uploaded successfully!`, severity: 'success' });
        
        // Auto-save after image upload to persist the URL
        setTimeout(() => {
          saveBrandingAfterImageUpload(updatedBranding);
        }, 100);
      } else {
        const errorText = await response.text();
        console.error('Upload response:', errorText);
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setSnackbar({ 
        open: true, 
        message: `Failed to upload ${isLogo ? 'logo' : 'banner'}: ${errorMessage}`, 
        severity: 'error' 
      });
      // Reset the file input
      event.target.value = '';
    } finally {
      if (isLogo) {
        setUploadingLogo(false);
      } else {
        setUploadingBanner(false);
      }
    }
  };

  const handleClearBrandingImage = async (asset: 'logo' | 'banner') => {
    if (!chatbot) return;

    const previousBranding: ChatbotConfig = { ...branding };
    const urlKey: 'logoUrl' | 'bannerUrl' = asset === 'logo' ? 'logoUrl' : 'bannerUrl';
    const materialKey: 'logoMaterialId' | 'bannerMaterialId' =
      asset === 'logo' ? 'logoMaterialId' : 'bannerMaterialId';
    const materialId = asset === 'logo' ? branding.logoMaterialId : branding.bannerMaterialId;
    const assetLabel = asset === 'logo' ? 'Logo' : 'Banner';
    const updatedBranding: ChatbotConfig = {
      ...branding,
      [urlKey]: '',
      [materialKey]: undefined,
    } as ChatbotConfig;

    if (!user) {
      setSnackbar({
        open: true,
        message: `Unable to remove ${assetLabel.toLowerCase()}: authentication required.`,
        severity: 'error',
      });
      return;
    }

    setRemovingAsset(asset);
    setBranding(updatedBranding);

    try {
      await saveBrandingAfterImageUpload(updatedBranding);
    } catch (error) {
      console.error(`[ChatbotStudio] Failed to remove ${assetLabel.toLowerCase()}:`, error);
      setBranding(previousBranding);
      const errorMessage =
        error instanceof Error ? error.message : `Failed to remove ${assetLabel.toLowerCase()}.`;
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      setRemovingAsset(null);
      return;
    }

    let deletionFailed = false;
    if (materialId) {
      try {
        const idToken = await user.getIdToken();
        if (!idToken) {
          throw new Error('Failed to authenticate request. Please refresh and try again.');
        }

        const deleteResponse = await fetch(`/api/v2/materials/${materialId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (!deleteResponse.ok) {
          throw new Error(`Failed to delete ${assetLabel.toLowerCase()} asset from storage.`);
        }
      } catch (error) {
        deletionFailed = true;
        console.error(
          `[ChatbotStudio] Failed to delete ${assetLabel.toLowerCase()} material from storage:`,
          error
        );
      }
    }

    setSnackbar({
      open: true,
      message: deletionFailed
        ? `${assetLabel} removed, but the stored asset could not be deleted. Please remove it manually from Materials.`
        : `${assetLabel} removed successfully!`,
      severity: deletionFailed ? 'error' : 'success',
    });

    setRemovingAsset(null);
  };

  const applyFunctionUpdate = (updatedFunction: any, fallbackBranding?: ChatbotConfig) => {
    if (!updatedFunction) return;

    setChatbot(updatedFunction);

    const serverBranding = updatedFunction.metadata?.branding;
    if (serverBranding || fallbackBranding) {
      const sourceBranding = serverBranding ?? fallbackBranding;
      setBranding(prev => ({
        ...prev,
        name: sourceBranding?.name || updatedFunction.display_name || updatedFunction.name || prev.name,
        description: sourceBranding?.description || updatedFunction.description || prev.description,
        primaryColor: normalizeHexColor(sourceBranding?.primaryColor) || prev.primaryColor || DEFAULT_BRANDING_COLORS.primaryColor,
        secondaryColor: normalizeHexColor(sourceBranding?.secondaryColor) || prev.secondaryColor || DEFAULT_BRANDING_COLORS.secondaryColor,
        accentColor: normalizeHexColor(sourceBranding?.accentColor) ?? prev.accentColor ?? DEFAULT_BRANDING_COLORS.accentColor,
        layoutStyle: ensureLayoutStyle(sourceBranding?.layoutStyle ?? prev.layoutStyle),
        logoUrl: sourceBranding?.logoUrl ?? prev.logoUrl,
        bannerUrl: sourceBranding?.bannerUrl ?? prev.bannerUrl,
        logoMaterialId: sourceBranding?.logoMaterialId ?? prev.logoMaterialId,
        bannerMaterialId: sourceBranding?.bannerMaterialId ?? prev.bannerMaterialId
      }));
    }

  if (updatedFunction.metadata?.show_credit_counter !== undefined) {
    setShowCreditCounter(updatedFunction.metadata.show_credit_counter);
  } else if (updatedFunction.metadata) {
    setShowCreditCounter(true);
  }

    if (updatedFunction.instructions) {
      setOriginalInstructions(updatedFunction.instructions);
      setEditedInstructions(updatedFunction.instructions);
    }

    // Update selected model if present in metadata
    if (updatedFunction.metadata?.selected_model) {
      setSelectedModel(updatedFunction.metadata.selected_model);
    } else if (updatedFunction.allowed_models && updatedFunction.allowed_models.length > 0) {
      setSelectedModel(updatedFunction.allowed_models[0]);
    }

    if (updatedFunction.id && updatedFunction.id !== functionId) {
      navigate(`/chatbot/studio/${updatedFunction.id}`, { replace: true });
    }
  };

  const saveBrandingAfterImageUpload = async (updatedBranding: ChatbotConfig) => {
    if (!chatbot) return;
    
    try {
      const idToken = await user?.getIdToken();

      console.log('[ChatbotStudio] Auto-saving after image upload:', updatedBranding);

      const sanitizedBranding: ChatbotConfig = {
        ...updatedBranding,
      layoutStyle: ensureLayoutStyle(updatedBranding.layoutStyle),
      primaryColor: normalizeHexColor(updatedBranding.primaryColor) || DEFAULT_BRANDING_COLORS.primaryColor,
      secondaryColor: normalizeHexColor(updatedBranding.secondaryColor) || DEFAULT_BRANDING_COLORS.secondaryColor,
      accentColor: normalizeHexColor(updatedBranding.accentColor) || DEFAULT_BRANDING_COLORS.accentColor,
      };
      
      const response = await fetch(`/api/v2/ai-functions/${chatbot.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: chatbot.name,
          display_name: chatbot.display_name,
          description: chatbot.description,
          instructions: editedInstructions,
          input_types: chatbot.input_types,
          allowed_models: chatbot.allowed_models,
          response_format: chatbot.response_format,
          metadata: {
            ...chatbot.metadata,
            branding: sanitizedBranding,
            chat_history_length: maxThreadLength,
            knowledge_chunks: knowledgeChunks,
            knowledge_categories: knowledgeCategories,
            intro_message: introMessage,
            override_title: overrideTitle,
            show_credit_counter: showCreditCounter,
            allow_attachments: allowAttachments,
            selected_model: selectedModel
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        applyFunctionUpdate(result.data || result, sanitizedBranding);
      } else {
        console.error('[ChatbotStudio] Failed to auto-save after image upload');
      }
    } catch (error) {
      console.error('[ChatbotStudio] Error auto-saving after image upload:', error);
    }
  };

  const saveBranding = async ({ suppressSuccessSnackbar = false }: { suppressSuccessSnackbar?: boolean } = {}) => {
    if (!chatbot) return false;
    
    setSaving(true);
    try {
      const idToken = await user?.getIdToken();

      const sanitizedBranding: ChatbotConfig = {
        ...branding,
        layoutStyle: ensureLayoutStyle(branding.layoutStyle),
        primaryColor: normalizeHexColor(branding.primaryColor) || DEFAULT_BRANDING_COLORS.primaryColor,
        secondaryColor: normalizeHexColor(branding.secondaryColor) || DEFAULT_BRANDING_COLORS.secondaryColor,
        accentColor: normalizeHexColor(branding.accentColor) || DEFAULT_BRANDING_COLORS.accentColor,
      };
      
      // Log the branding being saved
      console.log('Saving branding:', sanitizedBranding);
      
      const response = await fetch(`/api/v2/ai-functions/${chatbot.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: chatbot.name,
          display_name: chatbot.display_name,
          description: chatbot.description,
          instructions: chatbot.instructions,
          input_types: chatbot.input_types,
          allowed_models: chatbot.allowed_models,
          response_format: chatbot.response_format,
          metadata: {
            ...chatbot.metadata,
            branding: {
              name: sanitizedBranding.name,
              description: sanitizedBranding.description,
              primaryColor: sanitizedBranding.primaryColor,
              secondaryColor: sanitizedBranding.secondaryColor,
              accentColor: sanitizedBranding.accentColor,
              layoutStyle: sanitizedBranding.layoutStyle,
              logoUrl: sanitizedBranding.logoUrl || '',
              bannerUrl: sanitizedBranding.bannerUrl || '',
              logoMaterialId: sanitizedBranding.logoMaterialId,
              bannerMaterialId: sanitizedBranding.bannerMaterialId
            },
            chat_history_length: maxThreadLength,
            knowledge_chunks: knowledgeChunks,
            knowledge_categories: knowledgeCategories,
            intro_message: introMessage,
            override_title: overrideTitle,
            show_credit_counter: showCreditCounter,
            allow_attachments: allowAttachments,
            selected_model: selectedModel
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        const newVersion = result.data?.version || result.version;
        const instructionsChanged = originalInstructions !== editedInstructions;
        const message = instructionsChanged && newVersion
          ? `Instructions updated! Version ${newVersion} saved.`
          : 'Settings saved successfully!';
        if (!suppressSuccessSnackbar) {
          setSnackbar({ open: true, message, severity: 'success' });
        }
        
        const updatedFunction = (result.data || result) as any;
        if (updatedFunction) {
          applyFunctionUpdate(updatedFunction);
        } else if (instructionsChanged) {
          setOriginalInstructions(editedInstructions);
        }
        return true;
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // File size limits (in MB)
  const MAX_FILE_SIZE_MB = 20;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const MAX_FILES = 3;

  // Detect input type from file
  const detectInputType = (file: File): string => {
    const type = file.type.toLowerCase();
    const name = file.name.toLowerCase();
    
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'video';
    if (type.startsWith('audio/') || type === 'audio/mpeg' || name.endsWith('.mp3')) return 'audio';
    if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
    if (type === 'text/csv' || name.endsWith('.csv')) return 'csv';
    if (type === 'text/html' || name.endsWith('.html')) return 'html';
    if (type === 'text/markdown' || name.endsWith('.md')) return 'markdown';
    
    // Default to text for text files
    if (type.startsWith('text/')) return 'text';
    
    return 'binary';
  };

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: FileAttachment[] = [];
    
    for (let i = 0; i < Math.min(files.length, MAX_FILES - attachedFiles.length); i++) {
      const file = files[i];
      
      // Check file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        alert(`File "${file.name}" is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
        continue;
      }

      try {
        // Use unified file handler (automatically handles GCS upload for large files)
        const idToken = await user?.getIdToken();
        const prepared = await prepareFileForSubmission(file, undefined, idToken);

        const inputType = detectInputType(file);
        
        newAttachments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          dataUrl: prepared.input, // Will be base64 or GCS URL (now guaranteed to be string)
          inputType
        });
      } catch (error) {
        console.error(`Error reading file ${file.name}:`, error);
        alert(`Failed to read file "${file.name}"`);
      }
    }

    if (newAttachments.length > 0) {
      setAttachedFiles(prev => [...prev, ...newAttachments]);
    }

    // Reset input
    event.target.value = '';
  };

  // Remove attached file
  const removeAttachedFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const sendMessage = async () => {
    const text = input.trim();
    if ((!text && attachedFiles.length === 0) || sending || !chatbot) return;

    let activeThreadId = threadId;
    let activeThreadList = threads;
    if (!activeThreadId || !activeThreadList.some(t => t.id === activeThreadId)) {
      const fallbackThreadId = generateUUID();
      const chatbotName = overrideTitle || branding.name || chatbot?.display_name || chatbot?.name || 'New conversation';
      const fallbackThread: ChatThread = {
        id: fallbackThreadId,
        title: chatbotName,
        lastMessage: '',
        timestamp: new Date().toISOString(),
        messages: introMessage ? [{
          text: introMessage,
          isUser: false,
          timestamp: new Date().toISOString()
        }] : []
      };
      activeThreadList = [fallbackThread, ...activeThreadList];
      activeThreadId = fallbackThreadId;
      setThreads(prev => {
        const updated = [fallbackThread, ...prev];
        saveThreadsToStorage(updated);
        return updated;
      });
      setSelectedThread(fallbackThreadId);
      setThreadId(fallbackThreadId);
      setMessages(fallbackThread.messages);
    }

    const newMessage: Message = { 
      text, 
      isUser: true, 
      timestamp: new Date().toISOString(),
      attachments: attachedFiles.length > 0 ? [...attachedFiles] : undefined
    };
    
    // Check if this is the first user message
    const currentThread = activeThreadList.find(t => t.id === activeThreadId);
    const isFirstUserMessage = !!currentThread && currentThread.messages.filter(m => m.isUser).length === 0;
    
    // Update messages and thread
    setMessages(prev => [...prev, newMessage]);
    setThreads(prev => {
      const updated = prev.map(t => {
        if (t.id === activeThreadId) {
          const updatedThread = { 
            ...t, 
            messages: [...t.messages, newMessage],
            timestamp: new Date().toISOString()
          };
          if (isFirstUserMessage) {
            const title = text || (attachedFiles.length > 0 ? `[File: ${attachedFiles[0].name}]` : 'New conversation');
            updatedThread.title = title.slice(0, 30) + (title.length > 30 ? '...' : '');
          }
          return updatedThread;
        }
        return t;
      });
      saveThreadsToStorage(updated);
      return updated;
    });
    
    setInput('');
    const currentAttachments = [...attachedFiles];
    setAttachedFiles([]);
    setSending(true);

    try {
      const idToken = await user?.getIdToken();
      const customerTag = getEffectiveCustomerTag();
      
      // Prepare request based on whether we have files
      let requestBody: any;
      let inputType: string;
      
      if (currentAttachments.length > 0) {
        // If we have files, send the first file as input and text as message
        inputType = currentAttachments[0].inputType;
        requestBody = {
          input: currentAttachments[0].dataUrl,
          input_type: inputType,
          message: text || undefined, // Additional context
          metadata: {
            filename: currentAttachments[0].name,
            file_size: currentAttachments[0].size,
            content_type: currentAttachments[0].type
          }
        };
        
        // Add additional files as input1, input2, etc. if present
        if (currentAttachments.length > 1) {
          currentAttachments.slice(1).forEach((file, idx) => {
            (requestBody as any)[`input${idx + 1}`] = file.dataUrl;
          });
        }
      } else {
        // Text only
        inputType = 'text';
        requestBody = {
          input: text,
          input_type: 'text'
        };
      }
      
      // Add vector search if knowledge categories are set
      const resolvedKnowledgeCategories = Array.from(new Set(knowledgeCategories.filter(Boolean)));
      if (resolvedKnowledgeCategories.length > 0) {
        if (!requestBody.metadata) requestBody.metadata = {};
        requestBody.metadata.vector_search = {
          enabled: true,
          category_ids: resolvedKnowledgeCategories,
          top_k: Math.max(1, Math.min(knowledgeChunks || 2, 50))
        };
      }
      
      // Add selected model if specified
      if (selectedModel) {
        requestBody.model = selectedModel;
      }
      
      const headers: any = {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
        'EASYAI-THREAD-ID': activeThreadId,
        'EASYAI-MAX-THREAD-LEN': maxThreadLength.toString()
      };

      const response = await fetch(`/api/v2/functions/${customerTag}/${chatbot.name}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        const botResponse = data.result || data.response || 'No response received';
        const headerCredits = response.headers?.get('easyai-request-credits-used');
        const creditsUsed = headerCredits ? parseFloat(headerCredits) || 0 : data.usage?.credits || 0;
        
        const botMessage: Message = {
          text: botResponse,
          isUser: false,
          timestamp: new Date().toISOString(),
          credits: creditsUsed
        };
        
        setMessages(prev => [...prev, botMessage]);
        setThreads(prev => {
          const updated = prev.map(t =>
            t.id === activeThreadId
              ? {
                  ...t,
                  messages: [...t.messages, botMessage],
                  lastMessage: botResponse,
                  timestamp: new Date().toISOString()
                }
              : t
          );
          saveThreadsToStorage(updated);
          return updated;
        });
      } else {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        const errorMessage: Message = {
          text: `Error: ${response.status} - Failed to get response`,
          isUser: false,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
        setThreads(prev => {
          const updated = prev.map(t =>
            t.id === activeThreadId
              ? {
                  ...t,
                  messages: [...t.messages, errorMessage],
                  lastMessage: errorMessage.text,
                  timestamp: new Date().toISOString()
                }
              : t
          );
          saveThreadsToStorage(updated);
          return updated;
        });
      }
    } catch (error) {
      console.error('API Error:', error);
      const errorMessage: Message = {
        text: 'Sorry, I encountered an error. Please try again.',
        isUser: false,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
      setThreads(prev => {
        const updated = prev.map(t =>
          t.id === activeThreadId
            ? {
                ...t,
                messages: [...t.messages, errorMessage],
                lastMessage: errorMessage.text,
                timestamp: new Date().toISOString()
              }
            : t
        );
        saveThreadsToStorage(updated);
        return updated;
      });
    } finally {
      setSending(false);
    }
  };

  const generateIframeUrl = () => {
    const customerTag = getEffectiveCustomerTag();
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      chatbotId: chatbot.id,
      apiKey: selectedApiKey,
      customerTag: customerTag
    });
    
    console.log('[ChatbotStudio] Generating iframe URL with current branding:', {
      layoutStyle: branding.layoutStyle,
      bannerUrl: branding.bannerUrl,
      chatbotId: chatbot.id
    });
    
    return `${baseUrl}/embed/chatbot?${params.toString()}`;
  };
      
  const generateEmbedCode = (): string => {
    const customerTag = getEffectiveCustomerTag();
    const apiEndpoint = `https://${window.location.hostname}/api/v2/functions/${customerTag}/${chatbot.name}`;
    
    return `
// EasyAI Chatbot Embed Script
// Generated on ${new Date().toISOString()}
// Chatbot: ${branding.name}

(function() {
  // Configuration
  const CONFIG = {
    // TODO: Replace with your API key - DO NOT commit this to version control!
    apiKey: '${selectedApiKey}',
    apiEndpoint: '${apiEndpoint}',
    chatbotName: '${overrideTitle || branding.name}',
    brandDescription: '${branding.description || ''}',
    primaryColor: '${branding.primaryColor}',
    secondaryColor: '${branding.secondaryColor}',
    accentColor: '${branding.accentColor}',
    layoutStyle: '${branding.layoutStyle}',
    logoUrl: '${branding.logoUrl || ''}',
    bannerUrl: '${branding.bannerUrl || ''}',
    maxThreadLength: ${maxThreadLength},
    knowledgeCategories: ${JSON.stringify(knowledgeCategories)},
    knowledgeChunks: ${knowledgeChunks},
    introMessage: '${introMessage}',
    showCreditCounter: ${showCreditCounter},
    allowAttachments: ${allowAttachments},
    selectedModel: '${selectedModel || ''}'
  };

  // Create styles
  const styles = \`
    .easyai-chatbot-widget {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 9999;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    
    .easyai-chatbot-toggle {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: linear-gradient(135deg, \${CONFIG.primaryColor} 0%, \${CONFIG.secondaryColor} 100%);
      color: white;
      border: none;
      cursor: pointer;
            display: flex;
            align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      transition: transform 0.2s;
    }
    
    .easyai-chatbot-toggle:hover {
      transform: scale(1.1);
        }
    
    .easyai-chatbot-container {
      position: fixed;
      bottom: 90px;
      right: 20px;
      width: 380px;
      height: 600px;
            background: white;
      border-radius: 16px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.2);
      display: none;
      flex-direction: column;
            overflow: hidden;
      z-index: 9998;
      position: relative;
    }
    
    .easyai-chatbot-container.open {
      display: flex;
    }
    
    .easyai-thread-drawer {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 240px;
      background: #f8f9fa;
      box-shadow: 2px 0 5px rgba(0,0,0,0.1);
      transform: translateX(-100%);
      transition: transform 0.3s ease;
      z-index: 10;
            display: flex;
            flex-direction: column;
      padding-top: 60px;
        }
    
    .easyai-thread-drawer.open {
      transform: translateX(0);
        }

    .easyai-thread-drawer-profile {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 0 16px 12px 16px;
    }

    .easyai-thread-drawer-avatar {
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background: url('\${CONFIG.logoUrl}') center/cover;
      border: 2px solid \${CONFIG.layoutStyle === 'minimal' ? CONFIG.accentColor : CONFIG.primaryColor};
      box-shadow: 0 6px 14px rgba(0,0,0,0.18);
      flex-shrink: 0;
    }

    .easyai-thread-drawer-name {
      font-size: 13px;
      font-weight: 600;
      margin: 0;
      color: \${CONFIG.layoutStyle === 'minimal' ? '#2c2c2c' : CONFIG.primaryColor};
    }

    .easyai-thread-drawer-subtitle {
      font-size: 11px;
      color: #666;
      margin-top: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 140px;
    }
    
    .easyai-thread-drawer-header {
      padding: 15px;
      border-bottom: 1px solid #e0e0e0;
            background: white;
    }
    
    .easyai-thread-drawer-title {
      font-size: 16px;
            font-weight: 600;
      margin: 0 0 10px 0;
      color: #333;
    }
    
    .easyai-new-thread-btn {
      width: 100%;
      padding: 8px 15px;
      background: \${CONFIG.primaryColor};
      color: white;
      border: none;
      border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
      font-weight: 500;
      margin-bottom: 8px;
    }
    
    .easyai-new-thread-btn:hover {
      background: \${CONFIG.secondaryColor};
    }
    
    .easyai-thread-list {
      flex: 1;
      overflow-y: auto;
      padding: 0;
    }
    
    .easyai-thread-item {
      padding: 12px 15px;
      border-bottom: 1px solid #e0e0e0;
      cursor: pointer;
      position: relative;
      background: white;
      transition: background 0.2s;
    }
    
    .easyai-thread-item:hover {
      background: #f0f0f0;
    }
    
    .easyai-thread-item.active {
      background: #e8eaf6;
      border-left: 3px solid \${CONFIG.primaryColor};
        }
    
    .easyai-thread-title {
      font-size: 14px;
      font-weight: 500;
      margin: 0 0 4px 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .easyai-thread-time {
      font-size: 12px;
      color: #666;
    }
    
    .easyai-thread-delete {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: #999;
      cursor: pointer;
      padding: 4px;
      font-size: 18px;
      line-height: 1;
    }
    
    .easyai-thread-delete:hover {
      color: #f44336;
    }
    
    .easyai-close-drawer-btn:hover {
      background: rgba(0,0,0,0.1) !important;
      border-radius: 4px;
    }
    
    /* Header Styles */
    .easyai-chatbot-header {
      position: relative;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
    }
    
    .easyai-menu-btn {
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;
      padding: 6px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .easyai-menu-btn:hover {
      background: rgba(255,255,255,0.2);
    }
    
    /* Banner overrides all layout styles */
    .easyai-has-banner .easyai-chatbot-header {
      background: linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url('\${CONFIG.bannerUrl}');
      background-size: cover;
      background-position: center;
      color: white;
      padding: 26px 24px;
      min-height: 150px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      gap: 14px;
    }
    
    /* Modern Layout */
    .easyai-layout-modern:not(.easyai-has-banner) .easyai-chatbot-header {
      background: linear-gradient(135deg, \${CONFIG.primaryColor} 0%, \${CONFIG.secondaryColor} 100%);
      color: white;
      padding: 22px 26px;
      display: flex;
      align-items: center;
      gap: 16px;
    }
    
    .easyai-layout-modern .easyai-chatbot-logo {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: url('\${CONFIG.logoUrl}') center/cover;
      border: 2px solid white;
      flex-shrink: 0;
      \${!CONFIG.logoUrl || CONFIG.bannerUrl ? 'display: none;' : ''}
    }
    
    /* Minimal Layout */
    .easyai-layout-minimal:not(.easyai-has-banner) .easyai-chatbot-header {
      background: white;
      color: #333;
      border-bottom: 1px solid #e0e0e0;
      padding: 18px 22px;
      display: flex;
      align-items: center;
      gap: 14px;
    }
    
    .easyai-layout-minimal:not(.easyai-has-banner) .easyai-menu-btn:hover {
      background: rgba(0,0,0,0.05);
    }
    
    .easyai-layout-minimal .easyai-chatbot-logo {
      width: 34px;
      height: 34px;
      border-radius: 8px;
      background: url('\${CONFIG.logoUrl}') center/cover;
      \${!CONFIG.logoUrl || CONFIG.bannerUrl ? 'display: none;' : ''}
    }
    
    .easyai-chatbot-title {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
    }
    
    .easyai-chatbot-messages {
            flex: 1;
            overflow-y: auto;
            padding: 20px;
            background: #f8f9fa;
        }
    
    .easyai-message {
            margin-bottom: 15px;
            display: flex;
            align-items: flex-start;
        }
    
    .easyai-message.user {
      justify-content: flex-end;
    }
    
    .easyai-message-bubble {
            max-width: 70%;
            padding: 12px 16px;
            border-radius: 18px;
            line-height: 1.4;
            word-wrap: break-word;

            font-family: inherit;
        }
    
    .easyai-message.bot .easyai-message-bubble {
      background: \${CONFIG.layoutStyle === 'minimal' ? '#f0f0f0' : CONFIG.primaryColor};
      color: \${CONFIG.layoutStyle === 'minimal' ? '#333' : 'white'};
            border-bottom-left-radius: 4px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    .easyai-message.user .easyai-message-bubble {
      background: #e3f2fd;
      color: #1976d2;
      border: 1px solid #90caf9;
            border-bottom-right-radius: 4px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

    .easyai-message-bubble p {
      margin: 0;
    }

    .easyai-message-bubble p + p {
      margin-top: 4px;
    }

    .easyai-message-bubble ul,
    .easyai-message-bubble ol {
      margin: 4px 0 8px 20px;
      padding: 0;
    }

    .easyai-message-bubble li {
      margin: 2px 0;
      padding-left: 0;
    }

    .easyai-message-bubble li > p {
      display: inline;
      margin: 0;
    }

    .easyai-message-bubble code {
      font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
      font-size: 0.85em;
      padding: 0.2em 0.4em;
      border-radius: 4px;
      background: rgba(0,0,0,0.08);
      display: inline-block;
    }

    .easyai-message-bubble pre {
      font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
      font-size: 0.9em;
      padding: 12px;
      border-radius: 8px;
      margin: 8px 0;
      overflow-x: auto;
      background: rgba(0,0,0,0.12);
    }

    .easyai-message.user .easyai-message-bubble code {
      background: rgba(25,118,210,0.12);
      color: #0d47a1;
    }

    .easyai-message.user .easyai-message-bubble pre {
      background: rgba(25,118,210,0.15);
      color: #0d47a1;
    }

    .easyai-message.bot .easyai-message-bubble code,
    .easyai-message.bot .easyai-message-bubble pre {
      background: rgba(0,0,0,0.15);
      color: inherit;
    }

    .easyai-message-bubble a {
      color: inherit;
      text-decoration: underline;
    }
    
    .easyai-chatbot-input-area {
      padding: 15px;
            background: white;
            border-top: 1px solid #e0e0e0;
    }
    
    .easyai-attachments-preview {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 8px;
    }
    
    .easyai-attachment-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      background: #f0f0f0;
      border: 1px solid #e0e0e0;
      border-radius: 16px;
      font-size: 12px;
      max-width: 200px;
    }
    
    .easyai-attachment-chip-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .easyai-attachment-chip-remove {
      background: none;
      border: none;
      color: #666;
      cursor: pointer;
      font-size: 16px;
      line-height: 1;
      padding: 0;
      margin-left: 4px;
    }
    
    .easyai-attachment-chip-remove:hover {
      color: #f44336;
    }
    
    .easyai-chatbot-input {
            display: flex;
            gap: 8px;
            align-items: center;
        }
    
    .easyai-attach-btn {
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      padding: 10px;
      color: \${CONFIG.primaryColor};
      border-radius: 50%;
      transition: background 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 40px;
      height: 40px;
    }
    
    .easyai-attach-btn:hover {
      background: rgba(0,0,0,0.05);
    }
    
    .easyai-attach-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .easyai-chatbot-input input[type="text"] {
      flex: 1;
      padding: 10px 15px;
      border: 2px solid #e0e0e0;
      border-radius: 24px;
      font-size: 14px;
      outline: none;
      height: 40px;
      box-sizing: border-box;
    }
    
    .easyai-chatbot-input input[type="text"]:focus {
      border-color: \${CONFIG.primaryColor};
    }
    
    .easyai-chatbot-input button#easyai-send {
      padding: 10px 20px;
      background: \${CONFIG.accentColor};
      color: white;
      border: none;
      border-radius: 24px;
      cursor: pointer;
      font-weight: 600;
      height: 40px;
      min-height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .easyai-chatbot-input button#easyai-send:hover:not(:disabled) {
      filter: brightness(0.85);
    }
    
    .easyai-chatbot-input button#easyai-send:disabled {
      background: #e0e0e0;
      color: #9e9e9e;
      cursor: not-allowed;
    }
    
    .easyai-chatbot-input button.easyai-attach-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .easyai-credit-counter {
      text-align: center;
      color: #666;
      font-size: 11px;
      margin-top: 8px;
    }
    
    @media (max-width: 480px) {
      .easyai-chatbot-container {
        width: calc(100vw - 40px);
        height: calc(100vh - 110px);
      }
    }
  \`;
  
  // Create widget HTML
  const widgetHTML = \`
    <div class="easyai-chatbot-widget">
      <button class="easyai-chatbot-toggle" onclick="EasyAIChatbot.toggle()">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
          <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C13.19 22 14.34 21.78 15.41 21.37L19.07 22.87C19.21 22.93 19.36 22.96 19.51 22.96C19.85 22.96 20.17 22.81 20.39 22.54C20.66 22.21 20.76 21.76 20.64 21.34L19.77 18.33C21.17 16.72 22 14.62 22 12.33C22 6.68 17.52 2 12 2ZM16.5 12.75C16.09 12.75 15.75 12.41 15.75 12C15.75 11.59 16.09 11.25 16.5 11.25C16.91 11.25 17.25 11.59 17.25 12C17.25 12.41 16.91 12.75 16.5 12.75ZM12 12.75C11.59 12.75 11.25 12.41 11.25 12C11.25 11.59 11.59 11.25 12 11.25C12.41 11.25 12.75 11.59 12.75 12C12.75 12.41 12.41 12.75 12 12.75ZM7.5 12.75C7.09 12.75 6.75 12.41 6.75 12C6.75 11.59 7.09 11.25 7.5 11.25C7.91 11.25 8.25 11.59 8.25 12C8.25 12.41 7.91 12.75 7.5 12.75Z"/>
        </svg>
      </button>
      <div class="easyai-chatbot-container easyai-layout-\${CONFIG.layoutStyle} \${CONFIG.bannerUrl ? 'easyai-has-banner' : ''}" id="easyai-chatbot">
        <div class="easyai-thread-drawer" id="easyai-thread-drawer">
          <div class="easyai-thread-drawer-header">
            \${CONFIG.bannerUrl && CONFIG.logoUrl ? \`
            <div class="easyai-thread-drawer-profile">
              <div class="easyai-thread-drawer-avatar"></div>
              <div>
                <div class="easyai-thread-drawer-name">\${CONFIG.chatbotName}</div>
                \${CONFIG.brandDescription ? \`<div class="easyai-thread-drawer-subtitle">\${CONFIG.brandDescription}</div>\` : ''}
              </div>
            </div>
            \` : ''}
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <h3 class="easyai-thread-drawer-title">Previous Conversations</h3>
              <button class="easyai-close-drawer-btn" onclick="EasyAIChatbot.toggleThreadDrawer()" style="background: none; border: none; cursor: pointer; font-size: 24px; padding: 8px; line-height: 1; color: #333; position: relative; z-index: 1000;">×</button>
        </div>
            <button class="easyai-new-thread-btn" onclick="EasyAIChatbot.startNewThread()">New Conversation</button>
            <div style="font-size: 12px; color: #666;"><span id="easyai-thread-count">0</span> conversations</div>
            </div>
          <div class="easyai-thread-list" id="easyai-thread-list"></div>
        </div>
        <div class="easyai-chatbot-header">
          <button class="easyai-menu-btn" onclick="EasyAIChatbot.toggleThreadDrawer()">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
          \${CONFIG.logoUrl && !CONFIG.bannerUrl ? '<div class="easyai-chatbot-logo"></div>' : ''}
          <div style="flex: 1;">
            <h3 class="easyai-chatbot-title">\${CONFIG.chatbotName}</h3>
        </div>
    </div>
        <div class="easyai-chatbot-messages" id="easyai-messages">
          \${CONFIG.introMessage ? '<div class="easyai-message bot"><div class="easyai-message-bubble">' + CONFIG.introMessage + '</div></div>' : ''}
        </div>
        <div class="easyai-chatbot-input-area">
          <div id="easyai-attachments-preview" class="easyai-attachments-preview"></div>
          <div class="easyai-chatbot-input">
            \${CONFIG.allowAttachments ? '<input type="file" id="easyai-file-input" multiple accept="image/*,video/*,audio/*,.pdf,.csv,.html,.md,.txt,.doc,.docx" style="display: none;" /><button id="easyai-attach-btn" class="easyai-attach-btn" title="Attach files">📎</button>' : ''}
            <input type="text" id="easyai-input" placeholder="Type your message..." />
            <button id="easyai-send">Send</button>
          </div>
        \${CONFIG.showCreditCounter ? '<div class="easyai-credit-counter">Credits used: <span id="easyai-credits">0.00</span></div>' : ''}
        </div>
      </div>
    </div>
  \`;
  
  const loadExternalScript = (src) => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(\`script[src="\${src}"]\`)) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const ensureMarkdownSupport = (() => {
    let promise;
    return () => {
      if (window.marked && window.DOMPurify) return Promise.resolve();
      if (!promise) {
        promise = Promise.all([
          loadExternalScript('https://cdn.jsdelivr.net/npm/marked/marked.min.js'),
          loadExternalScript('https://cdn.jsdelivr.net/npm/dompurify@3.1.6/dist/purify.min.js')
        ])
          .then(() => {
            if (window.marked) {
              window.marked.setOptions({
                gfm: true,
                breaks: true,
                headerIds: false,
                mangle: false,
              });
            }
          })
          .catch((err) => {
            console.warn('[EasyAIChatbot] Failed to load Markdown helpers:', err);
          });
      }
      return promise;
    };
  })();

  const renderMarkdownHtml = (text) => {
    if (window.marked && window.DOMPurify) {
      const raw = window.marked.parse(text || '', { gfm: true, breaks: true });
      return window.DOMPurify.sanitize(raw);
    }
    const temp = document.createElement('div');
    temp.textContent = text || '';
    return temp.innerHTML.replace(/\\n/g, '<br/>');
  };

  // EasyAI Chatbot Class
  class EasyAIChatbot {
    constructor() {
      this.isOpen = false;
      this.showThreadDrawer = false;
      this.threads = [];
      this.currentThreadId = null;
      this.attachedFiles = [];
      this.MAX_FILES = 3;
      this.MAX_FILE_SIZE_MB = 20;
      this.MAX_FILE_SIZE_BYTES = this.MAX_FILE_SIZE_MB * 1024 * 1024;
      ensureMarkdownSupport();
      this.init();
        }

    generateUUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
                return v.toString(16);
            });
        }

    init() {
      // Inject styles
      const styleSheet = document.createElement('style');
      styleSheet.textContent = styles;
      document.head.appendChild(styleSheet);
      
      // Inject HTML
      const container = document.createElement('div');
      container.innerHTML = widgetHTML;
      document.body.appendChild(container);
      
      // Setup event listeners
      this.setupEventListeners();
      
      // Load threads from localStorage
      this.loadThreads();
      
      // Start with first thread or create new one
      if (this.threads.length === 0) {
        this.startNewThread();
      } else {
        this.selectThread(this.threads[0].id);
      }
    }
    
    setupEventListeners() {
      const input = document.getElementById('easyai-input');
      const sendBtn = document.getElementById('easyai-send');
      
      sendBtn.addEventListener('click', () => this.sendMessage());
      input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') this.sendMessage();
      });
      
      // Setup file attachment listeners if enabled
      if (CONFIG.allowAttachments) {
        const attachBtn = document.getElementById('easyai-attach-btn');
        const fileInput = document.getElementById('easyai-file-input');
        if (attachBtn && fileInput) {
          attachBtn.addEventListener('click', () => fileInput.click());
          fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
        }
      }
    }
    
    detectInputType(file) {
      const type = file.type.toLowerCase();
      const name = file.name.toLowerCase();
      
      if (type.startsWith('image/')) return 'image';
      if (type.startsWith('video/')) return 'video';
      if (type.startsWith('audio/') || type === 'audio/mpeg' || name.endsWith('.mp3')) return 'audio';
      if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
      if (type === 'text/csv' || name.endsWith('.csv')) return 'csv';
      if (type === 'text/html' || name.endsWith('.html')) return 'html';
      if (type === 'text/markdown' || name.endsWith('.md')) return 'markdown';
      if (type.startsWith('text/')) return 'text';
      
      return 'binary';
    }
    
    async handleFileSelect(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      
      for (let i = 0; i < Math.min(files.length, this.MAX_FILES - this.attachedFiles.length); i++) {
        const file = files[i];
        
        if (file.size > this.MAX_FILE_SIZE_BYTES) {
          alert(\`File "\${file.name}" is too large. Maximum size is \${this.MAX_FILE_SIZE_MB}MB.\`);
          continue;
        }
        
        try {
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          
          this.attachedFiles.push({
            name: file.name,
            size: file.size,
            type: file.type,
            dataUrl: dataUrl,
            inputType: this.detectInputType(file)
          });
        } catch (error) {
          console.error(\`Error reading file \${file.name}:\`, error);
          alert(\`Failed to read file "\${file.name}"\`);
        }
      }
      
      this.renderAttachments();
      event.target.value = '';
    }
    
    removeAttachment(index) {
      this.attachedFiles.splice(index, 1);
      this.renderAttachments();
    }
    
    renderAttachments() {
      const container = document.getElementById('easyai-attachments-preview');
      const attachBtn = document.getElementById('easyai-attach-btn');
      
      if (!CONFIG.allowAttachments) return;
      
      if (this.attachedFiles.length === 0) {
        container.innerHTML = '';
        container.style.display = 'none';
        if (attachBtn) attachBtn.disabled = false;
        return;
      }
      
      container.style.display = 'flex';
      if (attachBtn) attachBtn.disabled = this.attachedFiles.length >= this.MAX_FILES;
      
      container.innerHTML = this.attachedFiles.map((file, idx) => {
        const icon = file.inputType === 'image' ? '🖼️' :
                     file.inputType === 'video' ? '🎥' :
                     file.inputType === 'audio' ? '🎵' :
                     file.inputType === 'pdf' ? '📄' :
                     '📎';
        const sizeKB = (file.size / 1024).toFixed(1);
        return \`
          <div class="easyai-attachment-chip">
            <span>\${icon}</span>
            <span class="easyai-attachment-chip-name">\${file.name} (\${sizeKB} KB)</span>
            <button class="easyai-attachment-chip-remove" onclick="EasyAIChatbot.removeAttachment(\${idx})">×</button>
          </div>
        \`;
      }).join('');
    }
    
    toggle() {
      this.isOpen = !this.isOpen;
      const container = document.getElementById('easyai-chatbot');
      container.classList.toggle('open', this.isOpen);
      
      if (this.isOpen) {
        document.getElementById('easyai-input').focus();
      }
    }
    
    toggleThreadDrawer() {
      this.showThreadDrawer = !this.showThreadDrawer;
      const drawer = document.getElementById('easyai-thread-drawer');
      drawer.classList.toggle('open', this.showThreadDrawer);
    }
    
    loadThreads() {
      const saved = localStorage.getItem('easyai_threads_' + CONFIG.apiEndpoint);
      if (saved) {
        this.threads = JSON.parse(saved);
        this.renderThreadList();
      }
    }
    
    saveThreads() {
      localStorage.setItem('easyai_threads_' + CONFIG.apiEndpoint, JSON.stringify(this.threads));
        }

    getCurrentThread() {
      return this.threads.find(t => t.id === this.currentThreadId);
    }
    
    startNewThread() {
      const newThread = {
        id: this.generateUUID(),
        title: 'New conversation',
        messages: CONFIG.introMessage ? [{text: CONFIG.introMessage, isUser: false, timestamp: new Date().toISOString()}] : [],
        totalCredits: 0,
        timestamp: new Date().toISOString()
      };
      
      this.threads.unshift(newThread);
      this.saveThreads();
      this.selectThread(newThread.id);
      this.renderThreadList();
    }
    
    selectThread(threadId) {
      const thread = this.threads.find(t => t.id === threadId);
      if (!thread) return;
      
      this.currentThreadId = threadId;
      this.renderMessages();
      this.updateCreditCounter();
      this.renderThreadList();
    }
    
    deleteThread(threadId) {
      this.threads = this.threads.filter(t => t.id !== threadId);
      this.saveThreads();
      
      if (this.currentThreadId === threadId) {
        if (this.threads.length > 0) {
          this.selectThread(this.threads[0].id);
        } else {
          this.startNewThread();
        }
      }
      
      this.renderThreadList();
    }
    
    renderThreadList() {
      const listContainer = document.getElementById('easyai-thread-list');
      const countSpan = document.getElementById('easyai-thread-count');
      
      countSpan.textContent = this.threads.length;
      listContainer.innerHTML = '';
      
      this.threads.forEach(thread => {
        const item = document.createElement('div');
        item.className = 'easyai-thread-item';
        if (thread.id === this.currentThreadId) {
          item.classList.add('active');
        }
        
        // Use thread.title if it exists, otherwise use chatbot name, never use message text
        const displayTitle = thread.title || CONFIG.chatbotName || 'Conversation';
        
        item.innerHTML = \`
          <div class="easyai-thread-title">\${displayTitle}</div>
          <div class="easyai-thread-time">\${new Date(thread.timestamp).toLocaleString()}</div>
          <button class="easyai-thread-delete" onclick="event.stopPropagation(); EasyAIChatbot.deleteThread('\${thread.id}')">×</button>
        \`;
        
        item.onclick = () => this.selectThread(thread.id);
        listContainer.appendChild(item);
      });
    }
    
    renderMarkdownInto(element, text) {
      if (!element) return;
      element.innerHTML = renderMarkdownHtml(text);
      ensureMarkdownSupport().then(() => {
        element.innerHTML = renderMarkdownHtml(text);
      });
    }

    renderMessages() {
      const messagesContainer = document.getElementById('easyai-messages');
      const thread = this.getCurrentThread();
                    
      if (!thread) return;
      
      messagesContainer.innerHTML = '';
      thread.messages.forEach(msg => {
        const messageDiv = document.createElement('div');
        messageDiv.className = 'easyai-message ' + (msg.isUser ? 'user' : 'bot');
        const bubble = document.createElement('div');
        bubble.className = 'easyai-message-bubble';
        
        // Render file attachments if present
        if (msg.attachments && msg.attachments.length > 0) {
          const attachmentsDiv = document.createElement('div');
          attachmentsDiv.style.marginBottom = '8px';
          
          msg.attachments.forEach(file => {
            if (file.inputType === 'image') {
              const img = document.createElement('img');
              img.src = file.dataUrl;
              img.alt = file.name;
              img.style.maxWidth = '100%';
              img.style.maxHeight = '200px';
              img.style.borderRadius = '8px';
              img.style.cursor = 'pointer';
              img.onclick = () => window.open(file.dataUrl, '_blank');
              attachmentsDiv.appendChild(img);
            } else if (file.inputType === 'video') {
              const video = document.createElement('video');
              video.src = file.dataUrl;
              video.controls = true;
              video.style.maxWidth = '100%';
              video.style.maxHeight = '200px';
              video.style.borderRadius = '8px';
              attachmentsDiv.appendChild(video);
            } else if (file.inputType === 'audio') {
              const audio = document.createElement('audio');
              audio.src = file.dataUrl;
              audio.controls = true;
              audio.style.width = '100%';
              attachmentsDiv.appendChild(audio);
            } else {
              const fileChip = document.createElement('div');
              fileChip.style.cssText = 'display: inline-flex; align-items: center; gap: 4px; padding: 4px 8px; background: rgba(0,0,0,0.1); border-radius: 12px; font-size: 11px; margin-bottom: 4px;';
              const icon = file.inputType === 'pdf' ? '📄' : '📎';
              const sizeKB = (file.size / 1024).toFixed(1);
              fileChip.textContent = \`\${icon} \${file.name} (\${sizeKB} KB)\`;
              attachmentsDiv.appendChild(fileChip);
            }
          });
          
          bubble.appendChild(attachmentsDiv);
        }
        
        // Render text content
        if (msg.text) {
          const textDiv = document.createElement('div');
          textDiv.innerHTML = renderMarkdownHtml(msg.text);
          this.renderMarkdownInto(textDiv, msg.text);
          bubble.appendChild(textDiv);
        }
        
        messageDiv.appendChild(bubble);
        messagesContainer.appendChild(messageDiv);
      });
      
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
    
    updateCreditCounter() {
      if (!CONFIG.showCreditCounter) return;
      const counter = document.getElementById('easyai-credits');
      if (!counter) return;
      const thread = this.getCurrentThread();
      if (thread) {
        counter.textContent = thread.totalCredits.toFixed(2);
      }
    }
    
    async sendMessage() {
      const input = document.getElementById('easyai-input');
      const sendBtn = document.getElementById('easyai-send');
      const text = input.value.trim();
      
      if (!text && this.attachedFiles.length === 0) return;

      const thread = this.getCurrentThread();
      if (!thread) return;
      
      // Update thread title if this is first user message
      const isFirstUserMessage = !thread.messages.find(m => m.isUser);
      if (isFirstUserMessage) {
        const title = text || (this.attachedFiles.length > 0 ? \`[File: \${this.attachedFiles[0].name}]\` : 'New conversation');
        thread.title = title.slice(0, 30) + (title.length > 30 ? '...' : '');
      }
      
      // Add user message with attachments
      const userMsg = { 
        text, 
        isUser: true, 
        timestamp: new Date().toISOString(),
        attachments: this.attachedFiles.length > 0 ? [...this.attachedFiles] : undefined
      };
      thread.messages.push(userMsg);
      this.saveThreads();
      this.renderMessages();
      
      input.value = '';
      const currentAttachments = [...this.attachedFiles];
      this.attachedFiles = [];
      this.renderAttachments();
      sendBtn.disabled = true;

      try {
        let requestBody;
        
        if (currentAttachments.length > 0) {
          // Send file(s) with optional text message
          requestBody = {
            input: currentAttachments[0].dataUrl,
            input_type: currentAttachments[0].inputType,
            message: text || undefined,
            metadata: {
              filename: currentAttachments[0].name,
              file_size: currentAttachments[0].size,
              content_type: currentAttachments[0].type
            }
          };
          
          // Add additional files
          if (currentAttachments.length > 1) {
            currentAttachments.slice(1).forEach((file, idx) => {
              requestBody[\`input\${idx + 1}\`] = file.dataUrl;
            });
          }
        } else {
          // Text only
          requestBody = {
            input: text,
            input_type: 'text'
          };
        }
        
        // Add knowledge search if configured
        if (CONFIG.knowledgeCategories.length > 0) {
          if (!requestBody.metadata) requestBody.metadata = {};
          requestBody.metadata.vector_search = {
            enabled: true,
            category_ids: CONFIG.knowledgeCategories,
            top_k: CONFIG.knowledgeChunks
          };
        }
        
        // Add selected model if specified
        if (CONFIG.selectedModel) {
          requestBody.model = CONFIG.selectedModel;
        }
        
        const response = await fetch(CONFIG.apiEndpoint, {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + CONFIG.apiKey,
            'Content-Type': 'application/json',
            'EASYAI-THREAD-ID': thread.id,
            'EASYAI-MAX-THREAD-LEN': CONFIG.maxThreadLength.toString()
          },
          body: JSON.stringify(requestBody)
        });
        
        if (response.ok) {
          const data = await response.json();
          const botResponse = data.result || data.response || 'No response received';
          const headerCredits = response.headers.get('easyai-request-credits-used');
          const creditsUsed = headerCredits ? parseFloat(headerCredits) || 0 : data.usage?.credits || 0;
          
          const botMsg = { 
            text: botResponse, 
            isUser: false, 
            timestamp: new Date().toISOString(),
            credits: creditsUsed 
          };
          
          thread.messages.push(botMsg);
          if (CONFIG.showCreditCounter) {
            thread.totalCredits += creditsUsed;
          }
          thread.timestamp = new Date().toISOString();
          
          this.saveThreads();
          this.renderMessages();
          this.updateCreditCounter();
          this.renderThreadList();
        } else {
                    throw new Error('API request failed');
                }
            } catch (error) {
        console.error('Error sending message:', error);
        thread.messages.push({ 
          text: 'Sorry, I encountered an error. Please try again.', 
          isUser: false, 
          timestamp: new Date().toISOString() 
        });
        this.saveThreads();
        this.renderMessages();
      } finally {
            sendBtn.disabled = false;
            input.focus();
        }
    }
  }
  
  // Initialize chatbot
  window.EasyAIChatbot = new EasyAIChatbot();
})();
`;
  };

  const downloadCode = () => {
    if (!selectedApiKey) {
      setSnackbar({ open: true, message: 'Please select an API key first', severity: 'error' });
      return;
    }
    
    const code = generateEmbedCode();
    const blob = new Blob([code], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${branding.name.toLowerCase().replace(/\s+/g, '-')}-chatbot.js`;
    link.click();
    URL.revokeObjectURL(url);
    
    setSnackbar({ open: true, message: 'Downloaded chatbot embed code!', severity: 'success' });
  };

  const showIframe = () => {
    if (!selectedApiKey) {
      setSnackbar({ open: true, message: 'Please select an API key first', severity: 'error' });
      return;
    }
    setIframeUrl(generateIframeUrl());
    setShowIframeDialog(true);
  };

  const copyIframeCode = () => {
    const code = `<iframe src="${iframeUrl}" width="400" height="600" frameborder="0"></iframe>`;
    navigator.clipboard.writeText(code);
    setSnackbar({ open: true, message: 'Iframe code copied to clipboard!', severity: 'success' });
  };

  const renderLayoutPreview = () => ({
    modern: (
      <Box
        sx={{
          p: 3,
          bgcolor: 'grey.50',
          borderRadius: 2,
          border: '2px solid',
          borderColor: branding.layoutStyle === 'modern' ? branding.primaryColor : 'grey.300',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': { borderColor: branding.primaryColor }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: branding.primaryColor,
              backgroundImage: branding.logoUrl ? `url(${branding.logoUrl})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Modern Layout
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Gradient hero header with avatar
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            height: 130,
            bgcolor: 'white',
            borderRadius: 1,
            boxShadow: 1,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              height: 56,
              background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              gap: 1
            }}
          >
            <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: 'white' }} />
            <Box sx={{ height: 8, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1, flex: 1 }} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ height: 12, bgcolor: branding.primaryColor, borderRadius: 1, mb: 1, width: '70%' }} />
            <Box sx={{ height: 12, bgcolor: 'grey.200', borderRadius: 1, width: '50%' }} />
          </Box>
        </Box>
      </Box>
    ),
    minimal: (
      <Box
        sx={{
          p: 3,
          bgcolor: 'grey.50',
          borderRadius: 2,
          border: '2px solid',
          borderColor: branding.layoutStyle === 'minimal' ? branding.primaryColor : 'grey.300',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': { borderColor: branding.primaryColor }
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Minimal Layout
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Clean top bar with subtle accents
          </Typography>
        </Box>
        <Box
          sx={{
            height: 130,
            bgcolor: 'white',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              height: 40,
              bgcolor: 'white',
              borderBottom: '1px solid',
              borderColor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
              gap: 1
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: 1,
                bgcolor: branding.accentColor,
                backgroundImage: branding.logoUrl ? `url(${branding.logoUrl})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            <Box sx={{ height: 8, bgcolor: 'grey.300', borderRadius: 1, width: 90 }} />
          </Box>
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ height: 10, bgcolor: 'grey.100', borderRadius: 1, mb: 0.5 }} />
            <Box sx={{ height: 10, bgcolor: branding.accentColor, borderRadius: 1, width: '80%', opacity: 0.7 }} />
          </Box>
        </Box>
      </Box>
    )
  });

  const ColorPicker = ({
    label,
    value,
    defaultValue,
    onChange,
    onClear,
    positionAbove = false
  }: {
    label: string;
    value?: string;
    defaultValue: string;
    onChange: (color: string) => void;
    onClear: () => void;
    positionAbove?: boolean;
  }) => {
    const [showPicker, setShowPicker] = useState(false);
    const [tempColor, setTempColor] = useState('');
    const pickerRef = React.useRef<HTMLDivElement>(null);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const normalizedValue = value || defaultValue;

    // Initialize temp color when picker opens
    React.useEffect(() => {
      if (showPicker) {
        setTempColor(normalizedValue);
      }
    }, [showPicker, normalizedValue]);

    // Handle click outside to save and close
    React.useEffect(() => {
      if (!showPicker) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        
        // Don't close if clicking inside the picker or button
        if (pickerRef.current?.contains(target) || buttonRef.current?.contains(target)) {
          return;
        }
        
        // Save and close when clicking outside
        onChange(tempColor);
        setShowPicker(false);
      };

      // Add a small delay to avoid immediate close when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showPicker, tempColor, onChange]);

    const handleColorChange = (color: ColorResult) => {
      // Only update the temporary color while picking
      setTempColor(color.hex);
    };

    const handleApply = () => {
      // Apply the color
      onChange(tempColor);
      setShowPicker(false);
    };

    const handleCancel = () => {
      // Cancel without saving
      setTempColor(normalizedValue);
      setShowPicker(false);
    };

    // Use tempColor for display when picker is open, otherwise use normalizedValue
    const displayColor = showPicker ? tempColor : normalizedValue;

    return (
      <Box sx={{ position: 'relative' }}>
        <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {label}
        </Typography>
        
        {/* Main color display button */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
          <ButtonBase
            ref={buttonRef}
            onClick={() => setShowPicker(!showPicker)}
            disableRipple
            sx={{
              flex: 1,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: showPicker ? 'primary.main' : 'divider',
              p: 1.5,
              backgroundColor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              justifyContent: 'space-between',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
              }
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                border: '1px solid rgba(0,0,0,0.15)',
                backgroundColor: displayColor,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {displayColor.toUpperCase()}
            </Typography>
          </ButtonBase>
          {value !== defaultValue && (
            <IconButton size="small" onClick={() => { onClear(); }} title={`Reset ${label}`}>
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Compact color picker */}
        {showPicker && (
          <Box 
            ref={pickerRef}
            sx={{ 
              position: 'absolute',
              zIndex: 1300,
              ...(positionAbove ? { bottom: '100%', mb: 0.5 } : { mt: 0.5 }), 
              p: 1.5, 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'background.paper',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }}
          >
            <Stack spacing={2}>
              {/* ChromePicker from react-color */}
              <ChromePicker
                color={tempColor}
                onChange={handleColorChange}
                disableAlpha
                styles={{
                  default: {
                    picker: {
                      boxShadow: 'none',
                    }
                  }
                }}
              />

              {/* Compact actions */}
              <Stack 
                direction="row" 
                spacing={1} 
                sx={{ 
                  px: 0.5,
                  justifyContent: 'flex-end'
                }}
              >
                <Button 
                  onClick={handleCancel} 
                  size="small"
                  sx={{ 
                    minWidth: 70,
                    fontSize: '0.8125rem'
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleApply} 
                  variant="contained" 
                  size="small"
                  sx={{ 
                    minWidth: 70,
                    fontSize: '0.8125rem'
                  }}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>
    );
  };

  const ActionButtons = () => (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      sx={{ flexWrap: 'wrap', alignItems: { xs: 'stretch', sm: 'center' } }}
    >
      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={async () => {
          await saveBranding();
        }}
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
      <Button
        variant="outlined"
        startIcon={<WebsiteIcon />}
        onClick={() => setActiveTab(WEBSITE_TAB)}
      >
        Add to website
      </Button>
    </Stack>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
              </Box>
    );
  }

  if (!chatbot) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">Chatbot not found</Typography>
        <Button onClick={() => navigate('/dashboard?view=chatbot_generation')} sx={{ mt: 2 }}>
          Back to Chatbots
        </Button>
                  </Box>
    );
  }

  const getDrawerStyle = () => {
    if (branding.layoutStyle === 'minimal') {
      return {
        background: '#ffffff',
        backdropFilter: 'blur(18px)',
        borderRight: '1px solid #e5e5e5',
        boxShadow: '12px 0 32px -12px rgba(22, 34, 51, 0.16)',
        '& .MuiListItemButton-root': {
          borderRadius: 1.5,
          mx: 1,
          mt: 1,
          '&.Mui-selected': {
            bgcolor: alpha(branding.accentColor, 0.12),
            borderLeft: `3px solid ${branding.accentColor}`,
          },
          '&:hover': {
            bgcolor: alpha(branding.accentColor, 0.08),
          }
        }
      };
    }

    return {
      background: `linear-gradient(140deg, ${alpha(branding.primaryColor, 0.12)} 0%, ${alpha(
        branding.secondaryColor || branding.primaryColor,
        0.08
      )} 45%, rgba(255,255,255,0.75) 100%)`,
      backdropFilter: 'blur(22px)',
      borderRight: `1px solid ${alpha(branding.primaryColor, 0.25)}`,
      boxShadow: '16px 0 38px -14px rgba(20, 32, 56, 0.38)',
      '& .MuiListItemButton-root': {
        borderRadius: 1.5,
        mx: 1,
        mt: 1,
        '&.Mui-selected': {
          bgcolor: alpha(branding.primaryColor, 0.22),
          borderLeft: `3px solid ${branding.primaryColor}`,
        },
        '&:hover': {
          bgcolor: alpha(branding.primaryColor, 0.12),
        }
      }
    };
  };

  const renderChatInterface = () => {
    const getHeaderStyle = () => {
      if (branding.bannerUrl) {
        const isMinimal = branding.layoutStyle === 'minimal';
        return {
          background: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url(${branding.bannerUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          backgroundColor: 'transparent',
          borderBottom: 'none',
          borderColor: 'transparent',
          px: 3,
          py: 3.5,
          minHeight: 150,
          display: 'flex',
          gap: isMinimal ? 1.5 : 2,
          flexDirection: isMinimal ? 'column' : 'row',
          alignItems: isMinimal ? 'flex-start' : 'center',
          justifyContent: isMinimal ? 'flex-end' : 'space-between'
        };
      }

      if (branding.layoutStyle === 'minimal') {
        return {
          backgroundColor: 'white',
          color: 'text.primary',
          px: 2.5,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        };
      }

      return {
        background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`,
        color: 'white',
        backgroundColor: 'transparent',
        borderBottom: 'none',
        borderColor: 'transparent',
        px: 3,
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      };
    };

    const getLogoStyle = () => {
      const baseStyle = {
        backgroundImage: `url(${branding.logoUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
      
      if (branding.layoutStyle === 'minimal') {
        return {
          ...baseStyle,
          width: 34,
          height: 34,
          borderRadius: 1,
        };
      }

      return {
        ...baseStyle,
        width: 34,
        height: 34,
        borderRadius: '50%',
        border: '2px solid white',
        flexShrink: 0
      };
    };

    const drawerWidth = showThreadDrawer ? 280 : 0;

    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', minHeight: 0 }}>
        <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Chat Header */}
          <Box sx={getHeaderStyle()}>
            <IconButton 
              size="small" 
              sx={{ 
                color: branding.layoutStyle === 'minimal' && !branding.bannerUrl ? 'text.primary' : 'white',
                bgcolor: branding.layoutStyle === 'minimal' && !branding.bannerUrl ? 'grey.100' : 'rgba(255,255,255,0.1)',
                '&:hover': {
                  bgcolor: branding.layoutStyle === 'minimal' && !branding.bannerUrl ? 'grey.200' : 'rgba(255,255,255,0.2)',
                }
              }} 
              onClick={() => setShowThreadDrawer(!showThreadDrawer)}
            >
              {showThreadDrawer ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
            {branding.logoUrl && !branding.bannerUrl && (
              <Box sx={getLogoStyle()} />
                )}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color:
                    branding.bannerUrl || branding.layoutStyle === 'modern'
                      ? 'inherit'
                      : 'text.primary'
                }}
              >
                {overrideTitle || branding.name}
              </Typography>
              {branding.bannerUrl && introMessage && (
                <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                  {introMessage}
                </Typography>
              )}
            </Box>
              </Box>

              {/* Messages */}
          <Box
            ref={messagesContainerRef}
            sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
        p: 3,
        bgcolor: '#f8f9fa',
        ml: `${drawerWidth}px`,
        transition: 'margin 0.3s',
          }}
          >
                {messages.map((msg, idx) => (
                  <Box
                    key={idx}
                    sx={{
                  mb: 2,
                      display: 'flex',
                      justifyContent: msg.isUser ? 'flex-end' : 'flex-start'
                    }}
                  >
                    <Box sx={{
                      maxWidth: '70%',
                p: 2,
                      borderRadius: '18px',
                background: msg.isUser 
                  ? '#e3f2fd'
                  : (branding.layoutStyle === 'minimal' ? '#f0f0f0' : branding.primaryColor),
                color: msg.isUser 
                  ? '#1976d2' 
                  : (branding.layoutStyle === 'minimal' ? 'text.primary' : 'white'),
                border: msg.isUser ? '1px solid #90caf9' : 'none',
                      borderBottomLeftRadius: msg.isUser ? '18px' : '4px',
                      borderBottomRightRadius: msg.isUser ? '4px' : '18px',
                      whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '& p': { margin: 0, '&:not(:last-of-type)': { mb: 0.5 } },
                '& ul, & ol': { margin: '0.25rem 0 0.5rem 1.5rem', padding: 0 },
                '& li': {
                  margin: '0.15rem 0',
                  paddingLeft: 0,
                  '& > p': {
                    display: 'inline',
                    margin: 0
                  }
                },
                '& code': {
                  fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
                  fontSize: '0.85em',
                  padding: '0.2em 0.4em',
                  borderRadius: '4px',
                  backgroundColor: msg.isUser ? 'rgba(25,118,210,0.08)' : 'rgba(255,255,255,0.18)'
                },
                '& pre': {
                  fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
                  fontSize: '0.9em',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: msg.isUser ? 'rgba(25,118,210,0.12)' : 'rgba(0,0,0,0.2)',
                  overflowX: 'auto'
                },
                '& a': {
                  color: msg.isUser ? '#0d47a1' : (branding.layoutStyle === 'minimal' ? '#1976d2' : '#fff'),
                  textDecoration: 'underline'
                }
                    }}>
                {/* File attachments */}
                {msg.attachments && msg.attachments.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    {msg.attachments.map((file, fileIdx) => (
                      <Box key={fileIdx} sx={{ mb: 0.5 }}>
                        {file.inputType === 'image' ? (
                          <Box
                            component="img"
                            src={file.dataUrl}
                            alt={file.name}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: 300,
                              borderRadius: 2,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.9 }
                            }}
                            onClick={() => window.open(file.dataUrl, '_blank')}
                          />
                        ) : file.inputType === 'video' ? (
                          <Box
                            component="video"
                            controls
                            src={file.dataUrl}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: 300,
                              borderRadius: 2
                            }}
                          />
                        ) : file.inputType === 'audio' ? (
                          <Box
                            component="audio"
                            controls
                            src={file.dataUrl}
                            sx={{ width: '100%' }}
                          />
                        ) : (
                          <Chip
                            icon={
                              file.inputType === 'pdf' ? <PdfIcon /> :
                              file.inputType === 'csv' ? <FileIcon /> :
                              <FileIcon />
                            }
                            label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                            size="small"
                            sx={{ mb: 0.5 }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
                {msg.isUser ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {msg.text}
                        </Typography>
                      ) : (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeSanitize]}
                          components={{
                            p: (props: any) => <Typography variant="body2" {...props} />,
                            li: (props: any) => <li {...props} />,
                            strong: (props: any) => <strong {...props} />,
                            em: (props: any) => <em {...props} />,
                            code: ({ inline, className, children, ...props }: any) =>
                              inline ? (
                                <code className={className} {...props}>
                                  {children}
                                </code>
                              ) : (
                                <pre>
                                  <code className={className} {...props}>
                                    {children}
                                  </code>
                                </pre>
                              ),
                          }}
                        >
                          {msg.text}
                        </ReactMarkdown>
                      )}
                    </Box>
                  </Box>
                ))}
                {sending && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                    <Box sx={{
                  p: 2,
                      borderRadius: '18px',
                  background: branding.layoutStyle === 'minimal' ? '#f0f0f0' : branding.primaryColor,
                      display: 'flex',
                      gap: 0.5
                    }}>
                      {[0, 1, 2].map(i => (
                        <Box key={i} sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                      bgcolor: branding.layoutStyle === 'minimal' ? 'grey.500' : 'rgba(255,255,255,0.7)',
                          animation: 'bounce 1.4s infinite ease-in-out',
                          animationDelay: `${-0.32 + i * 0.16}s`
                        }} />
                      ))}
                    </Box>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area */}
              <Box sx={{
                p: 2,
                bgcolor: 'white',
                borderTop: '1px solid #e0e0e0',
            ml: `${drawerWidth}px`,
            transition: 'margin 0.3s',
              }}>
            {/* File Attachments Preview */}
            {attachedFiles.length > 0 && (
              <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {attachedFiles.map((file, idx) => (
                  <Chip
                    key={idx}
                    icon={
                      file.inputType === 'image' ? <ImageIcon /> :
                      file.inputType === 'video' ? <VideoIcon /> :
                      file.inputType === 'audio' ? <AudioIcon /> :
                      file.inputType === 'pdf' ? <PdfIcon /> :
                      <FileIcon />
                    }
                    label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                    onDelete={() => removeAttachedFile(idx)}
                    deleteIcon={<CloseIcon />}
                    color="primary"
                    variant="outlined"
                    sx={{ maxWidth: 250 }}
                  />
                ))}
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {allowAttachments && (
                  <>
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*,audio/*,.pdf,.csv,.html,.md,.txt,.doc,.docx"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      id="chatbot-file-input"
                      disabled={attachedFiles.length >= MAX_FILES}
                    />
                    <label htmlFor="chatbot-file-input" style={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        component="span"
                        disabled={sending || attachedFiles.length >= MAX_FILES}
                        sx={{
                          color: branding.primaryColor,
                          '&:hover': {
                            bgcolor: alpha(branding.primaryColor, 0.1)
                          }
                        }}
                        title={`Attach files (${attachedFiles.length}/${MAX_FILES})`}
                      >
                        <AttachFileIcon />
                      </IconButton>
                    </label>
                  </>
                )}
                <TextField
                  fullWidth
                size="medium"
                  placeholder="Type your message..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !sending && (input.trim() || attachedFiles.length > 0)) {
                      sendMessage();
                    }
                  }}
                  disabled={sending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '24px',
                  }
                }}
                />
                <Button
                  variant="contained"
                  onClick={sendMessage}
                  disabled={sending || (!input.trim() && attachedFiles.length === 0)}
                  startIcon={<SendIcon />}
                  sx={{
                    background: branding.accentColor,
                    borderRadius: '24px',
                    px: 3,
                    minHeight: '40px',
                    '&:hover': {
                      background: branding.accentColor,
                      filter: 'brightness(0.85)',
                    },
                    '&.Mui-disabled': {
                      background: '#e0e0e0',
                      color: '#9e9e9e',
                    },
                  }}
                >
                  Send
                </Button>
              </Box>
            {showCreditCounter && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                Credits used: {totalCreditsUsed.toFixed(2)}
              </Typography>
            )}
          </Box>

          {/* Thread Drawer */}
          <Drawer
            variant="persistent"
            anchor="left"
            open={showThreadDrawer}
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              position: 'absolute',
              height: '100%',
              '& .MuiDrawer-paper': {
                width: 280,
                position: 'absolute',
                height: '100%',
                boxSizing: 'border-box',
                pt: 8, // Add padding to avoid header overlap
                ...getDrawerStyle()
              },
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor:
                  branding.layoutStyle === 'minimal'
                    ? 'rgba(0,0,0,0.06)'
                    : alpha(branding.primaryColor, 0.25),
                bgcolor:
                  branding.layoutStyle === 'minimal'
                    ? 'rgba(255,255,255,0.95)'
                    : alpha(branding.primaryColor, 0.15),
                color:
                  branding.layoutStyle === 'minimal'
                    ? 'text.primary'
                    : alpha('#ffffff', 0.95),
                borderTopLeftRadius: 20,
                borderBottomLeftRadius: 20,
                boxShadow:
                  branding.layoutStyle === 'minimal'
                    ? '0 12px 24px rgba(15, 23, 42, 0.08)'
                    : '0 18px 34px rgba(20, 32, 56, 0.22)',
              }}
            >
              {branding.bannerUrl && branding.logoUrl && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      backgroundImage: `url(${branding.logoUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      border: `2px solid ${
                        branding.layoutStyle === 'minimal'
                          ? branding.accentColor || '#9c27b0'
                          : alpha(branding.primaryColor || '#9c27b0', 0.6)
                      }`,
                      boxShadow: '0 6px 14px rgba(0,0,0,0.15)',
                      flexShrink: 0
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                      {overrideTitle || branding.name}
                    </Typography>
                    {branding.description && (
                      <Typography variant="caption" color="textSecondary" noWrap>
                        {branding.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  color:
                    branding.layoutStyle === 'minimal'
                      ? 'text.primary'
                      : alpha('#ffffff', 0.95),
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, letterSpacing: 0.2 }}
                >
                  Previous Conversations
                </Typography>
                <IconButton 
                  size="small"
                  onClick={() => setShowThreadDrawer(false)}
                  sx={{
                    color:
                      branding.layoutStyle === 'minimal'
                        ? branding.primaryColor
                        : alpha('#ffffff', 0.9),
                    bgcolor:
                      branding.layoutStyle === 'minimal'
                        ? alpha(branding.primaryColor, 0.08)
                        : alpha('#000000', 0.1),
                    border: '1px solid',
                    borderColor:
                      branding.layoutStyle === 'minimal'
                        ? alpha(branding.primaryColor, 0.2)
                        : alpha('#ffffff', 0.25),
                    '&:hover': {
                      bgcolor:
                        branding.layoutStyle === 'minimal'
                          ? alpha(branding.primaryColor, 0.16)
                          : alpha('#000000', 0.2),
                    },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Box>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={startNewConversation}
                sx={{ 
                  mb: 1,
                  borderColor: branding.primaryColor,
                  color: branding.primaryColor,
                  '&:hover': {
                    borderColor: branding.primaryColor,
                    bgcolor: alpha(branding.primaryColor, 0.05),
                  }
                }}
              >
                New Conversation
              </Button>
              <Typography
                variant="caption"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color:
                    branding.layoutStyle === 'minimal'
                      ? 'text.secondary'
                      : alpha('#ffffff', 0.85),
                }}
              >
                <ChatIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                {threads.length} conversations
              </Typography>
            </Box>
            <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
              {threads.map((thread) => (
                <ListItemButton
                  key={thread.id}
                  selected={thread.id === selectedThread}
                  onClick={() => selectThread(thread)}
                  sx={{ pl: 2 }}
                >
                  <ListItemText
                    primary={thread.title}
                    secondary={new Date(thread.timestamp).toLocaleString()}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteThread(thread.id);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItemButton>
              ))}
            </List>
          </Drawer>
            </CardContent>
          </Card>
    );
  };

  const handleColorChange = (key: 'primaryColor' | 'secondaryColor' | 'accentColor') => (colorValue: string) => {
    const normalized = normalizeHexColor(colorValue) || DEFAULT_BRANDING_COLORS[key];
    setBranding(prev => ({
      ...prev,
      [key]: normalized,
    }));
  };

  const resetColor = (key: 'primaryColor' | 'secondaryColor' | 'accentColor') => () => {
    setBranding(prev => ({
      ...prev,
      [key]: DEFAULT_BRANDING_COLORS[key],
    }));
  };

  const headerDescription = getDescriptionSnippet(branding.description);
  // Saves first so Studio edits survive the trip; a save can move this page to a new version's URL.
  const goCreateKey = async () => {
    if (!(await saveBranding({ suppressSuccessSnackbar: true }))) return;
    navigate(`/dashboard/api-keys?${new URLSearchParams({
      for: branding.name || chatbot.display_name || chatbot.name || 'Chatbot',
      returnTo: `${window.location.pathname}?tab=website`,
    })}`);
  };

  const handlePreviewResize = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = previewHeight;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientY - startY;
      const next = Math.min(PREVIEW_MAX_HEIGHT, Math.max(PREVIEW_MIN_HEIGHT, startHeight + delta));
      setPreviewHeight(next);
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const runExportFlow = async (action: ExportAction, rememberChoice: boolean, viaDialog: boolean) => {
    if (viaDialog) {
      setExportConfirm(prev => ({ ...prev, loading: true }));
    }

    const success = await saveBranding({ suppressSuccessSnackbar: viaDialog });

    if (!success) {
      if (viaDialog) {
        setExportConfirm(prev => ({ ...prev, loading: false }));
      }
      return;
    }

    if (rememberChoice) {
      localStorage.setItem(EXPORT_WARNING_STORAGE_KEY, 'true');
    }

    if (action === 'download') {
      downloadCode();
    } else {
      showIframe();
    }

    if (viaDialog) {
      setExportConfirm({ open: false, action: null, remember: false, loading: false });
    }
  };

  const handleExportAction = (action: ExportAction) => {
    if (!selectedApiKey) {
      setSnackbar({ open: true, message: 'Please select an API key first', severity: 'error' });
      return;
    }

    const dismissed = localStorage.getItem(EXPORT_WARNING_STORAGE_KEY) === 'true';
    if (dismissed) {
      runExportFlow(action, false, false);
    } else {
      setExportConfirm({ open: true, action, remember: false, loading: false });
    }
  };

  const closeExportConfirm = () => {
    if (exportConfirm.loading) return;
    setExportConfirm({ open: false, action: null, remember: false, loading: false });
  };

  const confirmExportAction = () => {
    if (!exportConfirm.action) return;
    runExportFlow(exportConfirm.action, exportConfirm.remember, true);
  };

  return (
    <Box sx={{ maxWidth: 1800, mx: 'auto', py: 3, px: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/dashboard?view=chatbot_generation')}
            sx={{ textTransform: 'none', ml: -1, mb: 0.5 }}
          >
            Chatbots
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {branding.name || 'Chatbot'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            {headerDescription}
          </Typography>
        </Box>
        <Box sx={{ width: { xs: '100%', md: 'auto' }, ml: { md: 'auto' } }}>
          <ActionButtons />
        </Box>
      </Box>

      {justCreated && (
        <Alert severity="success" onClose={() => setJustCreated(false)} sx={{ mb: 3 }}>
          Your chatbot is ready. Chat with it in the preview, adjust how it behaves and looks on the left, and use{' '}
          <strong>Add to website</strong> when you're happy with it.
        </Alert>
      )}

      <SubscriptionGate 
        feature="Chatbot Studio"
        description="Create beautiful, branded chatbots with custom designs, embed them on your website, and provide amazing conversational experiences to your users."
      >

      <Grid container spacing={3}>
        {/* Left: Settings */}
        <Grid item xs={12} md={6} lg={5}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ mb: 2 }}
          >
            <Tab label="Behavior" />
            <Tab label="Look" />
            <Tab label="Add to website" />
          </Tabs>

          {activeTab === LOOK_TAB && (
            <>
              {/* Layout Selection */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <ViewModule sx={{ mr: 1 }} /> Layout Style
                  </Typography>
                  <ToggleButtonGroup
                    value={branding.layoutStyle}
                    exclusive
                    onChange={(e, value) => value && setBranding({ ...branding, layoutStyle: value })}
                    fullWidth
                    sx={{ mb: 3 }}
                  >
                    <ToggleButton value="modern">
                      <ModernIcon sx={{ mr: 0.5 }} /> Modern
                    </ToggleButton>
                    <ToggleButton value="minimal">
                      <MinimalIcon sx={{ mr: 0.5 }} /> Minimal
                    </ToggleButton>
                  </ToggleButtonGroup>
                  
                  <Grid container spacing={2}>
                    {Object.entries(renderLayoutPreview()).map(([key, layout]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Box
                          onClick={() => setBranding({ ...branding, layoutStyle: key as 'modern' | 'minimal' })}
                          sx={{ height: '100%' }}
                        >
                          {layout}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>

              {/* Branding */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <PaletteIcon sx={{ mr: 1 }} /> Colors & Images
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={5}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Brand Colors
                      </Typography>
                      <Stack spacing={1.5}>
                        <ColorPicker
                          label="Primary Color"
                          value={branding.primaryColor}
                          defaultValue={DEFAULT_BRANDING_COLORS.primaryColor}
                          onChange={handleColorChange('primaryColor')}
                          onClear={resetColor('primaryColor')}
                        />
                        <ColorPicker
                          label="Secondary Color"
                          value={branding.secondaryColor}
                          defaultValue={DEFAULT_BRANDING_COLORS.secondaryColor}
                          onChange={handleColorChange('secondaryColor')}
                          onClear={resetColor('secondaryColor')}
                        />
                        <ColorPicker
                          label="Accent Color"
                          value={branding.accentColor}
                          defaultValue={DEFAULT_BRANDING_COLORS.accentColor}
                          onChange={handleColorChange('accentColor')}
                          onClear={resetColor('accentColor')}
                          positionAbove={true}
                        />
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <Box
                        sx={{
                          display: { xs: 'block', md: 'none' },
                          my: 2,
                        }}
                      >
                        <Divider />
                      </Box>
                      <Stack spacing={3}>
                        {/* Logo Upload */}
                        <Box
                          sx={{
                            backgroundColor: alpha(branding.primaryColor, 0.05),
                            borderRadius: 2,
                            p: 2,
                            border: `1px solid ${alpha(branding.primaryColor, 0.12)}`,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Logo Image
                          </Typography>
                          <Alert severity="info" sx={{ mb: 1, py: 0.75 }}>
                            <Typography variant="caption">
                              <strong>Recommended:</strong> {LAYOUT_IMAGE_SPECS[branding.layoutStyle].logo.recommended}
                              <br />
                              <strong>Format:</strong> {LAYOUT_IMAGE_SPECS[branding.layoutStyle].logo.shape}
                              <br />
                              {LAYOUT_IMAGE_SPECS[branding.layoutStyle].logo.description}
                            </Typography>
                          </Alert>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={uploadingLogo ? <CircularProgress size={16} /> : <UploadIcon />}
                            fullWidth
                            disabled={uploadingLogo}
                          >
                            {uploadingLogo ? 'Uploading...' : branding.logoUrl ? 'Change Logo' : 'Upload Logo'}
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => handleImageUpload('logoUrl', e)}
                              disabled={uploadingLogo}
                            />
                          </Button>
                          {branding.logoUrl && (
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                              <img src={branding.logoUrl} alt="Logo" style={{ maxWidth: 110, maxHeight: 110 }} />
                              <IconButton
                                size="small"
                                onClick={() => handleClearBrandingImage('logo')}
                                disabled={removingAsset === 'logo' || uploadingLogo}
                              >
                                {removingAsset === 'logo' ? <CircularProgress size={16} /> : <ClearIcon />}
                              </IconButton>
                            </Box>
                          )}
                        </Box>

                        {/* Banner Upload */}
                        <Box
                          sx={{
                            backgroundColor: alpha(branding.secondaryColor || branding.primaryColor, 0.05),
                            borderRadius: 2,
                            p: 2,
                            border: `1px solid ${alpha(
                              branding.secondaryColor || branding.primaryColor,
                              0.12
                            )}`,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Banner Image
                          </Typography>
                          <Alert severity="info" sx={{ mb: 1, py: 0.75 }}>
                            <Typography variant="caption">
                              <strong>Recommended:</strong> {LAYOUT_IMAGE_SPECS[branding.layoutStyle].banner.recommended}
                              <br />
                              <strong>Format:</strong> {LAYOUT_IMAGE_SPECS[branding.layoutStyle].banner.shape}
                              <br />
                              {LAYOUT_IMAGE_SPECS[branding.layoutStyle].banner.description}
                            </Typography>
                          </Alert>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={uploadingBanner ? <CircularProgress size={16} /> : <UploadIcon />}
                            fullWidth
                            disabled={uploadingBanner}
                          >
                            {uploadingBanner ? 'Uploading...' : branding.bannerUrl ? 'Change Banner' : 'Upload Banner'}
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => handleImageUpload('bannerUrl', e)}
                              disabled={uploadingBanner}
                            />
                          </Button>
                          {branding.bannerUrl && (
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                              <img
                                src={branding.bannerUrl}
                                alt="Banner"
                                style={{ width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 8 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleClearBrandingImage('banner')}
                                disabled={removingAsset === 'banner' || uploadingBanner}
                              >
                                {removingAsset === 'banner' ? <CircularProgress size={16} /> : <ClearIcon />}
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

            </>
          )}

          {activeTab === BEHAVIOR_TAB && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>What it does</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Changes reach the preview and your website after you click <strong>Save changes</strong>.
                </Typography>

                <TextField
                  fullWidth
                  label="Instructions"
                  value={editedInstructions}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditedInstructions(value);
                    setChatbot((prev: any) => (prev ? { ...prev, instructions: value } : prev));
                  }}
                  multiline
                  minRows={6}
                  maxRows={16}
                  size="small"
                  sx={{ mb: 2 }}
                  helperText="Who it talks to, what it should and shouldn't answer, its tone, and what to do when it doesn't know. Saving new instructions creates a new version."
                />

                <TextField
                  fullWidth
                  label="Welcome message (optional)"
                  placeholder="E.g. Hi! Ask me anything about your order."
                  value={introMessage}
                  onChange={(e) => setIntroMessage(e.target.value)}
                  multiline
                  rows={2}
                  size="small"
                  sx={{ mb: 2 }}
                  helperText="The first message people see when they open the chat."
                />

                <TextField
                  fullWidth
                  label="Title in the chat window (optional)"
                  placeholder="Leave empty to use the chatbot's name"
                  value={overrideTitle}
                  onChange={(e) => setOverrideTitle(e.target.value)}
                  size="small"
                />

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Documents it answers from</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Knowledge Hub categories it searches on every message.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {knowledgeCategories.map(catId => {
                    const cat = categories.find(c => c.id === catId);
                    return cat ? (
                      <Chip
                        key={catId}
                        label={cat.name}
                        size="small"
                        onDelete={() => setKnowledgeCategories(prev => prev.filter(id => id !== catId))}
                      />
                    ) : null;
                  })}
                  {knowledgeCategories.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      None yet. It answers from its instructions only.
                    </Typography>
                  )}
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setShowCategorySelector(true)}>
                    Choose categories
                  </Button>
                </Box>

                {knowledgeCategories.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Passages to look up per message: {knowledgeChunks}
                    </Typography>
                    <Slider
                      value={knowledgeChunks}
                      onChange={(e, value) => setKnowledgeChunks(value as number)}
                      min={1}
                      max={10}
                      step={1}
                      marks={[
                        { value: 1, label: '1' },
                        { value: 3, label: '3' },
                        { value: 5, label: '5' },
                        { value: 8, label: '8' },
                        { value: 10, label: '10' },
                      ]}
                      valueLabelDisplay="auto"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      More passages give fuller answers but use more credits (each adds roughly 100 to 500 tokens).
                    </Typography>
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary">
                  Add or update documents in the{' '}
                  <Link component={RouterLink} to="/dashboard/materials" underline="hover">
                    Knowledge Hub
                  </Link>
                  .
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Conversation</Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Messages it remembers: {maxThreadLength}
                  </Typography>
                  <Slider
                    value={maxThreadLength}
                    onChange={(e, value) => setMaxThreadLength(value as number)}
                    min={5}
                    max={50}
                    step={5}
                    marks={[
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                      { value: 25, label: '25' },
                      { value: 50, label: '50' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    How many earlier messages it reads before answering.
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allowAttachments}
                      onChange={(e) => setAllowAttachments(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Let people attach files"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                  Images, documents and other files.
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showCreditCounter}
                      onChange={(e) => setShowCreditCounter(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Show credits used"
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                  A running total in the chat window. Useful while testing; most sites turn it off.
                </Typography>

                {chatbot?.allowed_models && chatbot.allowed_models.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>AI model</Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel>Model</InputLabel>
                      <Select
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                        label="Model"
                      >
                        {chatbot.allowed_models.map((model: string) => (
                          <MenuItem key={model} value={model}>
                            {model}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      The model that writes its answers.
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === WEBSITE_TAB && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Put it on your website</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Your latest changes are saved first, so your website gets what you see in the preview.
                </Typography>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>1. Choose the API key it uses</Typography>
                {apiKeys.length === 0 ? (
                  <Alert
                    severity="warning"
                    sx={{ mb: 1.5 }}
                    action={
                      <Button onClick={goCreateKey} disabled={saving} color="inherit" size="small">
                        Create key
                      </Button>
                    }
                  >
                    You need an API key first. Create one just for this chatbot; your changes are saved and you'll come
                    straight back here.
                  </Alert>
                ) : (
                  <FormControl fullWidth size="small" sx={{ mb: 0.5 }}>
                    <InputLabel>API key</InputLabel>
                    <Select
                      value={selectedApiKey}
                      onChange={(e) => setSelectedApiKey(e.target.value)}
                      label="API key"
                    >
                      {apiKeys.map((key) => (
                        <MenuItem key={key.id} value={key.key}>
                          {key.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {apiKeys.length > 0 && (
                  <Link
                    component="button"
                    type="button"
                    onClick={goCreateKey}
                    disabled={saving}
                    variant="body2"
                    underline="hover"
                    sx={{ display: 'block', mb: 1.5 }}
                  >
                    Create a separate key for this chatbot
                  </Link>
                )}
                <Alert severity="info" sx={{ mb: 3 }}>
                  Anyone visiting your site can see this key. Use a separate key with a monthly limit just for this
                  chatbot, so you can cap it or turn it off without affecting anything else.
                </Alert>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>2. Get the code</Typography>
                <Stack spacing={1.5}>
                  <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Chat button (recommended)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        A JavaScript file you add to your site with one script tag. It puts a chat button in the
                        bottom-right corner of every page that loads it.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<CodeIcon />}
                      onClick={() => handleExportAction('download')}
                      disabled={!selectedApiKey}
                    >
                      Download script
                    </Button>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Chat window on one page (iframe)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Shows the chat inside a box where you paste the code, for example on a Help page.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={() => handleExportAction('iframe')}
                      disabled={!selectedApiKey}
                    >
                      Get code
                    </Button>
                  </Paper>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right: Live Preview */}
        <Grid item xs={12} md={6} lg={7}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box
              sx={{
                height: previewHeight,
                minHeight: PREVIEW_MIN_HEIGHT,
                maxHeight: PREVIEW_MAX_HEIGHT,
                transition: 'height 0.12s ease-out',
                borderRadius: 3,
                overflow: 'hidden',
              }}
              key={`chat-preview-${branding.layoutStyle}-${branding.bannerUrl ? 'with-banner' : 'no-banner'}`}
            >
              {renderChatInterface()}
            </Box>
            <Box
              onMouseDown={handlePreviewResize}
              sx={{
                cursor: 'ns-resize',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                color: 'text.secondary',
                py: 0.75,
                backgroundColor: 'background.paper',
                '&:hover': {
                  borderColor: branding.primaryColor,
                  color: branding.primaryColor,
                },
              }}
            >
              <DragHandleIcon fontSize="small" />
              <Typography variant="caption">Drag to resize preview height</Typography>
            </Box>
            <Box>
              <ActionButtons />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={exportConfirm.open}
        onClose={closeExportConfirm}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={exportConfirm.loading}
      >
        <DialogTitle>Save Before Exporting?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            We’ll save your latest chatbot settings before {exportConfirm.action === 'download' ? 'downloading the JavaScript embed file' : 'generating the iframe code'}. Continue?
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={exportConfirm.remember}
                onChange={(_, checked) => setExportConfirm(prev => ({ ...prev, remember: checked }))}
                disabled={exportConfirm.loading}
              />
            }
            label="Don’t show this again"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeExportConfirm} disabled={exportConfirm.loading}>
            Cancel
          </Button>
          <Button
            onClick={confirmExportAction}
            variant="contained"
            disabled={exportConfirm.loading}
            startIcon={exportConfirm.loading ? <CircularProgress size={16} /> : undefined}
          >
            {exportConfirm.loading ? 'Saving…' : 'Save & Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Iframe Dialog */}
      {/* Category Selector Dialog */}
      <Dialog 
        open={showCategorySelector} 
        onClose={() => setShowCategorySelector(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Select Knowledge Categories</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select which categories your chatbot should search when answering questions
          </Typography>
          {categories.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                No categories found. Create categories and upload materials from the{' '}
                <Button 
                  size="small" 
                  variant="text" 
                  onClick={() => navigate('/dashboard/materials')}
                  sx={{ p: 0, minWidth: 'auto', verticalAlign: 'baseline' }}
                >
                  Knowledge Hub
                </Button>
              </Typography>
            </Alert>
          ) : (
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {categories.map((cat) => (
                <FormControlLabel
                  key={cat.id}
                  control={
                    <Checkbox
                      checked={knowledgeCategories.includes(cat.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setKnowledgeCategories(prev => [...prev, cat.id]);
                        } else {
                          setKnowledgeCategories(prev => prev.filter(id => id !== cat.id));
                        }
                      }}
                      color="primary"
                    />
                  }
                  label={cat.name}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCategorySelector(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setShowCategorySelector(false);
              setSnackbar({ open: true, message: 'Categories updated. Click "Save changes" to apply.', severity: 'success' });
            }} 
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showIframeDialog} onClose={() => setShowIframeDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Embed Your Chatbot</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              The iframe embed requires the embedded page to be deployed. Make sure your chatbot embed page is accessible at the URL shown below.
            </Typography>
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Use this iframe code to embed your chatbot on any website:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={`<iframe src="${iframeUrl}" width="400" height="600" frameborder="0" style="border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"></iframe>`}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="textSecondary">
            Iframe URL: {iframeUrl}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowIframeDialog(false)}>Close</Button>
          <Button onClick={copyIframeCode} variant="contained" startIcon={<CopyIcon />}>
            Copy Code
          </Button>
        </DialogActions>
      </Dialog>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}</style>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </SubscriptionGate>
    </Box>
  );
};

export default ChatbotStudio;
